import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertTriangle,
  ArrowRight,
  Bookmark,
  Briefcase,
  Building2,
  Handshake,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Filter,
  ListChecks,
  Pencil,
  Search,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import BackButton from "../shared/backButton";
import Pagination from "../shared/paginator/paginator";
import BulkAction from "../shared/utils/bulkAction";
import MessageManager from "../layout/Message";
import {
  readPartnerCollaborationState,
  subscribePartnerCollaborationState,
} from "../shared/utils/partnerCollaborationState";
import {
  addProfilesToRoleShortlist,
  getProfileShortlistFlag,
  readHiringShortlists,
  removeProfilesFromRoleShortlist,
  renameRoleShortlist,
  subscribeHiringShortlists,
} from "../shared/utils/hiringShortlistState";
import "./HiringPage.css";

import {
  getJobRolesSharedByPartnerApi,
  getPartnersListApi,
  getMatchesForjobRoleApi,
  shortListPersonForJobRoleApi,
  removePersonFromShortlistApi,
  getTop3RoleMatchesOfUserIdApi,
  openShortlistedJobRoleApi,
  getShortlistedJobRolesApi,
  updateShortlistNameApi,
  contactPartnerApi,
  notifyInterestApi,
} from "../../services/hiringService";
import { getDepartmentsListApi } from "../../services/settingsService";
import { getAllJobRolesApi } from "../../services/jobRolesService";

const ROLE_PAGE_SIZE = 6;
const ROLE_PAGE_SIZE_OPTIONS = [6, 12, 24, 48];
const ROLE_DESCRIPTION_PREVIEW_LENGTH = 180;
const ROLE_LEVEL_FILTER_OPTIONS = [
  "Executive or Senior Management",
  "Middle Management",
  "First-Level Management",
  "Intermediate or Experienced (Senior Staff)",
  "Entry-Level",
];

const getRoleShortDescription = (role) =>
  role?.shortDescription || role?.description || "";

const getRoleFullDescription = (role) =>
  role?.fullDescription || role?.longDescription || role?.description || "";

const getRoleDescriptionPreview = (description = "") => {
  const normalizedDescription = String(description || "").trim();

  if (normalizedDescription.length <= ROLE_DESCRIPTION_PREVIEW_LENGTH) {
    return normalizedDescription;
  }

  const clippedDescription = normalizedDescription.slice(
    0,
    ROLE_DESCRIPTION_PREVIEW_LENGTH,
  );

  const lastWordBoundary = clippedDescription.lastIndexOf(" ");

  const preview =
    lastWordBoundary > ROLE_DESCRIPTION_PREVIEW_LENGTH * 0.65
      ? clippedDescription.slice(0, lastWordBoundary)
      : clippedDescription;

  return `${preview.trim()}...`;
};
const MATCH_PAGE_SIZE = 5;
const MATCH_PAGE_SIZE_OPTIONS = [5, 10, 20, 40];
const MATCH_UNKNOWN_AVAILABILITY_FILTER = "__MATCH_NULL_AVAILABILITY__";
const MATCH_SOURCE_FILTER_OPTIONS = ["Your Organization", "Partner Shared"];
const MATCH_AVAILABILITY_FILTER_OPTIONS = [
  "Available",
  "Pending Confirmation",
  "Partially Available",
  "Not Available",
];

const HIRING_ACTIVE_ROLE_KEY = "mmhHiringActiveRole";
const HIRING_ACTIVE_VIEW_KEY = "mmhHiringActiveView";
const HIRING_OPEN_SHORTLIST_KEY = "mmhHiringOpenShortlistRole";
const SHORTLIST_PAGE_SIZE = 5;
const SHORTLIST_PAGE_SIZE_OPTIONS = [5, 10, 20, 40];
const SHORTLIST_OVERVIEW_PAGE_SIZE = 6;
const SHORTLIST_OVERVIEW_PAGE_SIZE_OPTIONS = [6, 12, 24, 48];

const isOrganizationMatchSource = (source) => {
  const normalizedSource = String(source || "")
    .trim()
    .toLowerCase();

  return ["internal", "organization", "your organization", "your org"].includes(
    normalizedSource,
  );
};

const isProtectedExternalProfileName = (name) =>
  String(name || "")
    .trim()
    .toUpperCase()
    .startsWith("EXT");

const getUniqueMatchValues = (values) =>
  Array.from(
    new Set(values.map((value) => String(value ?? "").trim()).filter(Boolean)),
  );

const getMatchAvailabilityRequestValue = (availabilityFilter) => {
  if (availabilityFilter === "All") return "";
  if (availabilityFilter === MATCH_UNKNOWN_AVAILABILITY_FILTER) return null;

  return availabilityFilter;
};

const normalizeMatchProfile = (profile, index) => {
  const source = String(profile?.source || "").trim();
  const isInternal = isOrganizationMatchSource(source);

  const id = String(
    profile?._id ||
      profile?.userId ||
      profile?.externalCode ||
      `match-profile-${index + 1}`,
  );

  const displayId = String(profile?.name || "");

  const numericMatch = Number(
    profile?.totalMatchPercentage ??
      profile?.matchPercentage ??
      profile?.match ??
      0,
  );

  return {
    id,
    displayId,
    userId: profile?.userId || "",
    externalCode: profile?.externalCode || "",
    name: profile?.name || "",
    email: profile?.email || "",
    title: profile?.title || profile?.designation || "",
    department: profile?.department || "",
    match: Number.isFinite(numericMatch) ? numericMatch : 0,
    availability: profile?.availability ?? null,
    source,
    isInternal,
    partner: isInternal
      ? null
      : profile?.partnerOrganizationName ||
        profile?.partnerOrganization ||
        profile?.partnerName ||
        profile?.partner ||
        null,
    partnerId: profile?.partnerTenantId || profile?.partnerId || "",
    partnerTenantId: profile?.partnerTenantId || "",
    saved: getProfileShortlistFlag(profile),
    rawProfile: profile,
  };
};

const getMatchProfileUserId = (profile) =>
  String(
    profile?.userId || profile?.rawProfile?.userId || profile?.id || "",
  ).trim();

const ensureShortlistApiSuccess = (response) => {
  const code = Number(response?.code);

  if (Number.isFinite(code) && ![200, 201].includes(code)) {
    throw new Error(response?.message || "The shortlist request failed.");
  }

  return response;
};

const getMatchesArrayFromResponse = (response) =>
  [
    response,
    response?.data,
    response?.data?.data,
    response?.result,
    response?.data?.result,
    response?.matches,
    response?.data?.matches,
    response?.items,
    response?.data?.items,
    response?.content,
    response?.data?.content,
    response?.data?.data?.matches,
    response?.data?.data?.items,
    response?.data?.data?.content,
  ].find(Array.isArray);

const getMatchesTotalFromResponse = (
  response,
  currentPage,
  pageSize,
  currentPageCount,
) => {
  const rawTotal =
    response?.totalCount ??
    response?.data?.totalCount ??
    response?.data?.data?.totalCount ??
    response?.totalElements ??
    response?.data?.totalElements ??
    response?.data?.data?.totalElements ??
    response?.totalItems ??
    response?.data?.totalItems ??
    response?.data?.data?.totalItems ??
    response?.totalRecords ??
    response?.data?.totalRecords ??
    response?.data?.data?.totalRecords ??
    response?.count ??
    response?.data?.count ??
    response?.data?.data?.count ??
    response?.total ??
    response?.data?.total ??
    response?.data?.data?.total ??
    response?.pagination?.totalCount ??
    response?.data?.pagination?.totalCount ??
    response?.data?.data?.pagination?.totalCount ??
    response?.pageInfo?.totalCount ??
    response?.data?.pageInfo?.totalCount ??
    response?.data?.data?.pageInfo?.totalCount;

  const numericTotal = Number(rawTotal);

  if (Number.isFinite(numericTotal) && numericTotal >= 0) {
    return numericTotal;
  }

  // When an older response returns only an array, keep the next page
  // available if the current page is full.
  return currentPageCount < pageSize
    ? (currentPage - 1) * pageSize + currentPageCount
    : currentPage * pageSize + 1;
};

const getOpenShortlistProfilesFromResponse = (response) =>
  [
    response?.data?.profiles,
    response?.profiles,
    response?.data?.data?.profiles,
    response?.data?.data?.data?.profiles,
  ].find(Array.isArray) || [];

const getOpenShortlistSummaryFromResponse = (response) =>
  [
    response?.data?.shortlist,
    response?.shortlist,
    response?.data?.data?.shortlist,
    response?.result?.shortlist,
    response?.data?.result?.shortlist,
  ].find(Boolean) || null;

const getOpenShortlistTotalFromResponse = (
  response,
  shortlistSummary,
  profilesCount,
) => {
  const rawTotal =
    response?.totalCount ??
    response?.data?.totalCount ??
    response?.data?.data?.totalCount ??
    shortlistSummary?.shortlistedProfilesCount ??
    shortlistSummary?.profilesCount ??
    shortlistSummary?.totalCount ??
    profilesCount;

  const numericTotal = Number(rawTotal);

  return Number.isFinite(numericTotal) && numericTotal >= 0
    ? numericTotal
    : profilesCount;
};

const normalizeOpenShortlistResponse = (response, fallbackRole = {}) => {
  const shortlistSummary = getOpenShortlistSummaryFromResponse(response) || {};
  const profiles = getOpenShortlistProfilesFromResponse(response);
  const normalizedProfiles = profiles.map(normalizeMatchProfile);
  const roleId =
    fallbackRole?.id ||
    fallbackRole?._id ||
    shortlistSummary?.roleId ||
    shortlistSummary?.jobId ||
    "";

  const role = {
    ...fallbackRole,
    id: String(roleId),
    jobId: String(shortlistSummary?.jobId || fallbackRole?.jobId || ""),
    title: shortlistSummary?.roleName || fallbackRole?.title || "Untitled role",
    department: shortlistSummary?.department || fallbackRole?.department || "",
    level: shortlistSummary?.level || fallbackRole?.level || "",
    source: fallbackRole?.source || "",
  };

  const name =
    shortlistSummary?.shortlistName ||
    fallbackRole?.name ||
    `${role.title} Shortlist`;

  const organizationProfilesCount = Number(
    shortlistSummary?.organizationProfilesCount ??
      normalizedProfiles.filter((profile) => profile.isInternal).length,
  );
  const partnerSharedProfilesCount = Number(
    shortlistSummary?.partnerSharedProfilesCount ??
      normalizedProfiles.filter((profile) => !profile.isInternal).length,
  );
  const shortlistedProfilesCount = Number(
    shortlistSummary?.shortlistedProfilesCount ?? normalizedProfiles.length,
  );

  return {
    shortlist: shortlistSummary,
    role,
    name,
    profiles: normalizedProfiles,
    totalCount: getOpenShortlistTotalFromResponse(
      response,
      shortlistSummary,
      normalizedProfiles.length,
    ),
    updatedAt:
      shortlistSummary?.updatedDate ||
      shortlistSummary?.createdDate ||
      fallbackRole?.updatedAt ||
      new Date().toISOString(),
    organizationProfilesCount: Number.isFinite(organizationProfilesCount)
      ? organizationProfilesCount
      : 0,
    partnerSharedProfilesCount: Number.isFinite(partnerSharedProfilesCount)
      ? partnerSharedProfilesCount
      : 0,
    shortlistedProfilesCount: Number.isFinite(shortlistedProfilesCount)
      ? shortlistedProfilesCount
      : normalizedProfiles.length,
  };
};

const getShortlistedJobRolesFromResponse = (response) =>
  [
    response?.data,
    response?.data?.data,
    response?.result,
    response?.data?.result,
    response?.items,
    response?.data?.items,
    response?.jobRoles,
    response?.data?.jobRoles,
    response?.shortlists,
    response?.data?.shortlists,
  ].find(Array.isArray) || [];

const normalizeShortlistedJobRoleSummary = (shortlist, index) => {
  const id = String(
    shortlist?._id || shortlist?.jobId || `shortlist-role-${index + 1}`,
  );
  const jobId = String(shortlist?.jobId || "");
  const roleName = String(shortlist?.roleName || "Untitled role");
  const shortlistName = String(
    shortlist?.shortlistName || `${roleName} Shortlist`,
  );

  return {
    id,
    jobId,
    shortlistId: String(shortlist?._id || ""),
    name: shortlistName,
    role: {
      id,
      jobId,
      title: roleName,
      department: shortlist?.department || "",
      level: shortlist?.level || "",
    },
    updatedAt:
      shortlist?.updatedDate ||
      shortlist?.createdDate ||
      new Date().toISOString(),
    organizationProfilesCount: Number(
      shortlist?.organizationProfilesCount ?? 0,
    ),
    partnerSharedProfilesCount: Number(
      shortlist?.partnerSharedProfilesCount ?? 0,
    ),
    shortlistedProfilesCount: Number(shortlist?.shortlistedProfilesCount ?? 0),
    rawShortlist: shortlist,
  };
};

const getShortlistedJobRolesTotalFromResponse = (
  response,
  currentPageCount,
) => {
  const rawTotal =
    response?.totalCount ??
    response?.data?.totalCount ??
    response?.data?.data?.totalCount ??
    response?.total ??
    response?.data?.total ??
    response?.data?.data?.total ??
    response?.count ??
    response?.data?.count ??
    response?.data?.data?.count ??
    response?.totalRecords ??
    response?.data?.totalRecords ??
    response?.data?.data?.totalRecords ??
    currentPageCount;

  const numericTotal = Number(rawTotal);

  return Number.isFinite(numericTotal) && numericTotal >= 0
    ? numericTotal
    : currentPageCount;
};

const HIRING_ONBOARDING_SEEN_KEY = "maya-hiring-onboarding-seen";

const getHiringOnboardingStorageKey = () => {
  if (typeof window === "undefined") {
    return `${HIRING_ONBOARDING_SEEN_KEY}:default`;
  }

  const organizationKey =
    sessionStorage.getItem("tenantId") ||
    sessionStorage.getItem("mainTenantId") ||
    "default";

  return `${HIRING_ONBOARDING_SEEN_KEY}:${organizationKey}`;
};

const HIRING_ONBOARDING_STEPS = [
  {
    title: "Select a Role",
    description:
      "Choose an open role from your organization or review a role shared by an active partner.",
    icon: Briefcase,
  },
  {
    title: "Review and Shortlist Matches",
    description:
      "Compare matching profiles, review fit information, and add suitable profiles to a role-specific shortlist.",
    icon: ListChecks,
  },
  {
    title: "Share with Partners",
    description:
      "Use active partner connections to share approved roles and profiles for collaborative hiring.",
    icon: Handshake,
  },
];

const readStoredHiringRole = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedRole = sessionStorage.getItem(HIRING_ACTIVE_ROLE_KEY);
    return storedRole ? JSON.parse(storedRole) : null;
  } catch (error) {
    console.error("Unable to restore the selected hiring role:", error);
    return null;
  }
};

const HiringPage = () => {
  const navigate = useNavigate();
  const partnerLookupRef = useRef(null);
  const matchPartnerLookupRef = useRef(null);
  const organizationRolesRequestKeyRef = useRef("");
  const partnerRolesRequestKeyRef = useRef("");
  const partnersRequestStartedRef = useRef(false);
  const departmentsRequestStartedRef = useRef(false);
  const selectedShortlistNameRef = useRef("");
  const shortlistOverviewRequestKeyRef = useRef("");

  const restoredActiveRole = useMemo(() => readStoredHiringRole(), []);

  const [activeRoleTab, setActiveRoleTab] = useState(() =>
    restoredActiveRole?.source === "Partner" ? "partner" : "organization",
  );

  const [selectedRoleId, setSelectedRoleId] = useState(
    restoredActiveRole?.id || "",
  );
  const [organizationRoles, setOrganizationRoles] = useState([]);
  const [organizationRolesTotal, setOrganizationRolesTotal] = useState(0);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState("");
  const [roleItemsPerPage, setRoleItemsPerPage] = useState(ROLE_PAGE_SIZE);
  const [partnerRoles, setPartnerRoles] = useState([]);
  const [partnerRolesTotal, setPartnerRolesTotal] = useState(0);
  const [partnerRolesLoading, setPartnerRolesLoading] = useState(false);
  const [partnerRolesError, setPartnerRolesError] = useState("");
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [activeRole, setActiveRole] = useState(restoredActiveRole);
  const [descriptionRole, setDescriptionRole] = useState(null);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [roleSearchInputValue, setRoleSearchInputValue] = useState("");
  const [showRoleSearchBar, setShowRoleSearchBar] = useState(false);
  const [showRoleFiltersPanel, setShowRoleFiltersPanel] = useState(false);
  const [roleDepartmentFilter, setRoleDepartmentFilter] = useState("All");
  const [roleLevelFilter, setRoleLevelFilter] = useState("All");
  const [rolePartnerFilter, setRolePartnerFilter] = useState("All");
  const [rolePartnerLookup, setRolePartnerLookup] = useState("");
  const [showPartnerLookup, setShowPartnerLookup] = useState(false);
  const [showOnlyOpenRoles, setShowOnlyOpenRoles] = useState(false);
  const [rolePageByTab, setRolePageByTab] = useState({
    organization: 1,
    partner: 1,
  });

  const [matches, setMatches] = useState([]);
  const [matchesTotal, setMatchesTotal] = useState(0);
  const [matchSourceValues, setMatchSourceValues] = useState([]);
  const [matchAvailabilityValues, setMatchAvailabilityValues] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchSelectAllLoading, setMatchSelectAllLoading] = useState(false);
  const [shortlistActionProfileId, setShortlistActionProfileId] = useState("");
  const [shortlistBulkActionLoading, setShortlistBulkActionLoading] =
    useState(false);
  const [matchesError, setMatchesError] = useState("");
  const [matchSearchInputValue, setMatchSearchInputValue] = useState("");
  const [matchSearchTerm, setMatchSearchTerm] = useState("");
  const [showMatchSearchBar, setShowMatchSearchBar] = useState(false);
  const [showMatchFiltersPanel, setShowMatchFiltersPanel] = useState(false);
  const [filterSource, setFilterSource] = useState("All");
  const [filterPartner, setFilterPartner] = useState("All");
  const [matchPartnerLookup, setMatchPartnerLookup] = useState("");
  const [showMatchPartnerLookup, setShowMatchPartnerLookup] = useState(false);
  const [filterAvailability, setFilterAvailability] = useState("All");
  const [filterMatch, setFilterMatch] = useState("All");
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [matchSort, setMatchSort] = useState({
    field: "match",
    direction: "desc",
  });
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [selectedMatchProfiles, setSelectedMatchProfiles] = useState([]);
  const [matchCurrentPage, setMatchCurrentPage] = useState(1);
  const [matchItemsPerPage, setMatchItemsPerPage] = useState(MATCH_PAGE_SIZE);
  const [partnerCollaboration, setPartnerCollaboration] = useState(
    readPartnerCollaborationState,
  );
  const [showHiringOnboarding, setShowHiringOnboarding] = useState(false);
  const [hiringOnboardingStep, setHiringOnboardingStep] = useState(0);
  const [hiringView, setHiringView] = useState(
    () => sessionStorage.getItem(HIRING_ACTIVE_VIEW_KEY) || "matches",
  );
  const [shortlists, setShortlists] = useState(readHiringShortlists);
  const [selectedShortlistApiState, setSelectedShortlistApiState] =
    useState(null);
  const [selectedShortlistRoleSnapshot, setSelectedShortlistRoleSnapshot] =
    useState(null);
  const [selectedShortlistRoleId, setSelectedShortlistRoleId] = useState(
    () => sessionStorage.getItem(HIRING_OPEN_SHORTLIST_KEY) || "",
  );
  const [shortlistSearchTerm, setShortlistSearchTerm] = useState("");
  const [shortlistSearchInput, setShortlistSearchInput] = useState("");
  const [showShortlistSearch, setShowShortlistSearch] = useState(false);
  const [showShortlistFilters, setShowShortlistFilters] = useState(false);
  const [shortlistSourceFilter, setShortlistSourceFilter] = useState("All");
  const [shortlistAvailabilityFilter, setShortlistAvailabilityFilter] =
    useState("All");
  const [shortlistPartnerFilter, setShortlistPartnerFilter] = useState("All");
  const [shortlistOverviewRoles, setShortlistOverviewRoles] = useState([]);
  const [shortlistOverviewTotal, setShortlistOverviewTotal] = useState(0);
  const [shortlistOverviewLoading, setShortlistOverviewLoading] =
    useState(false);
  const [shortlistOverviewError, setShortlistOverviewError] = useState("");
  const [shortlistOverviewLoaded, setShortlistOverviewLoaded] = useState(false);
  const [shortlistOverviewCurrentPage, setShortlistOverviewCurrentPage] =
    useState(1);
  const [shortlistOverviewItemsPerPage, setShortlistOverviewItemsPerPage] =
    useState(SHORTLIST_OVERVIEW_PAGE_SIZE);
  const [shortlistProfiles, setShortlistProfiles] = useState([]);
  const [shortlistTotal, setShortlistTotal] = useState(0);
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const [shortlistSelectAllLoading, setShortlistSelectAllLoading] =
    useState(false);
  const [shortlistError, setShortlistError] = useState("");
  const [shortlistSelectedIds, setShortlistSelectedIds] = useState([]);
  const [shortlistSelectedProfiles, setShortlistSelectedProfiles] = useState(
    [],
  );
  const [shortlistCurrentPage, setShortlistCurrentPage] = useState(1);
  const [shortlistItemsPerPage, setShortlistItemsPerPage] =
    useState(SHORTLIST_PAGE_SIZE);
  const [shortlistOverviewSearchInput, setShortlistOverviewSearchInput] =
    useState("");

  const [shortlistOverviewSearchTerm, setShortlistOverviewSearchTerm] =
    useState("");

  const [showShortlistOverviewSearch, setShowShortlistOverviewSearch] =
    useState(false);

  const [showShortlistOverviewFilters, setShowShortlistOverviewFilters] =
    useState(false);

  const [shortlistOverviewDepartment, setShortlistOverviewDepartment] =
    useState("All");

  const [shortlistOverviewLevel, setShortlistOverviewLevel] = useState("All");
  const [renamingShortlist, setRenamingShortlist] = useState(false);
  const [shortlistNameInput, setShortlistNameInput] = useState("");

  // Load the partner directory once and reuse it across partner filters.
  const getPartnerDataList = useCallback(async () => {
    // React Strict Mode can run mount effects twice in development.
    if (partnersRequestStartedRef.current) {
      return;
    }

    partnersRequestStartedRef.current = true;

    try {
      setPartnersLoading(true);
      setPartnersError("");

      const response = await getPartnersListApi();

      const partnersData = [
        response,
        response?.data,
        response?.data?.data,
        response?.result,
        response?.data?.result,
        response?.partners,
        response?.data?.partners,
        response?.items,
        response?.data?.items,
      ].find(Array.isArray);

      if (!partnersData) {
        throw new Error(
          "The partners API returned an invalid response format.",
        );
      }

      const normalizedPartners = partnersData
        .filter((partner) => partner?._id)
        .map((partner) => ({
          id: String(partner._id),
          partnerOrganization:
            partner.partnerOrganization ||
            partner.organizationName ||
            partner.partnerOrgCode ||
            "Unnamed partner",
          partnerOrgCode: partner.partnerOrgCode || "",
          partnerTenantId:
            partner.partnerTenantId ||
            partner.tenantId ||
            partner.partnerId ||
            "",
          partnerSuperAdminName: partner.partnerSuperAdminName || "",
          partnerSuperAdminEmail: partner.partnerSuperAdminEmail || "",
          status: String(partner.status || "").toUpperCase(),
          invitation: partner.invitation || "",
          protectProfileIdentity: Boolean(partner.protectProfileIdentity),
          sharedData: Array.isArray(partner.sharedData)
            ? partner.sharedData
            : [],
          rawPartner: partner,
        }))
        .filter(
          (partner, index, list) =>
            list.findIndex((item) => item.id === partner.id) === index,
        )
        .sort((first, second) =>
          first.partnerOrganization.localeCompare(second.partnerOrganization),
        );

      setPartners(normalizedPartners);
    } catch (error) {
      // Allow retrying the same request after an error.
      partnersRequestStartedRef.current = false;
      console.error("Error fetching partner data list:", error);
      setPartners([]);
      setPartnersError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load partners.",
      );
    } finally {
      setPartnersLoading(false);
    }
  }, []);

  const getDepartmentsListApiHandler = useCallback(async () => {
    if (departmentsRequestStartedRef.current) {
      return;
    }

    departmentsRequestStartedRef.current = true;

    try {
      const response = await getDepartmentsListApi();
      const departmentsData =
        [
          response?.data,
          response?.data?.data,
          response?.departments,
          response?.data?.departments,
          response,
        ].find(Array.isArray) || [];

      setDepartmentOptions(
        getUniqueMatchValues(
          departmentsData.map((department) => department?.name || department),
        ),
      );
    } catch (error) {
      departmentsRequestStartedRef.current = false;
      console.error("Error fetching departments:", error);
      setDepartmentOptions([]);
    }
  }, []);

  // API call to get partner-shared roles using server-side pagination.
  const handlePartnerSharedRolesApi = useCallback(
    async (currentPage = 1, itemsPerPage = ROLE_PAGE_SIZE) => {
      // These keys directly match the controls available in the Partner Roles UI.
      const requestData = {
        search: roleSearchTerm.trim(),
        department: roleDepartmentFilter === "All" ? "" : roleDepartmentFilter,
        level: roleLevelFilter === "All" ? "" : roleLevelFilter,
        partnerId: rolePartnerFilter === "All" ? "" : rolePartnerFilter,
        pageNo: currentPage,
        pageSize: itemsPerPage,
      };

      const requestKey = JSON.stringify(requestData);

      // Prevent duplicate requests, including React Strict Mode effect re-runs.
      if (partnerRolesRequestKeyRef.current === requestKey) {
        return;
      }

      partnerRolesRequestKeyRef.current = requestKey;

      try {
        setPartnerRolesLoading(true);
        setPartnerRolesError("");

        const response = await getJobRolesSharedByPartnerApi(requestData);

        // Support the direct array returned by the current API and common wrappers.
        const rolesData = [
          response,
          response?.data,
          response?.data?.data,
          response?.result,
          response?.data?.result,
          response?.jobRoles,
          response?.data?.jobRoles,
          response?.content,
          response?.data?.content,
          response?.items,
          response?.data?.items,
          response?.data?.data?.content,
          response?.data?.data?.items,
          response?.data?.data?.jobRoles,
        ].find(Array.isArray);

        if (!rolesData) {
          throw new Error(
            "The partner-shared roles API returned an invalid response format.",
          );
        }

        const rawTotal =
          response?.totalCount ??
          response?.data?.totalCount ??
          response?.data?.data?.totalCount ??
          response?.totalElements ??
          response?.data?.totalElements ??
          response?.data?.data?.totalElements ??
          response?.totalItems ??
          response?.data?.totalItems ??
          response?.data?.data?.totalItems ??
          response?.totalRecords ??
          response?.data?.totalRecords ??
          response?.data?.data?.totalRecords ??
          response?.count ??
          response?.data?.count ??
          response?.data?.data?.count ??
          response?.total ??
          response?.data?.total ??
          response?.data?.data?.total ??
          response?.pagination?.totalCount ??
          response?.data?.pagination?.totalCount ??
          response?.data?.data?.pagination?.totalCount ??
          response?.pagination?.total ??
          response?.data?.pagination?.total ??
          response?.data?.data?.pagination?.total ??
          response?.pageInfo?.totalCount ??
          response?.data?.pageInfo?.totalCount ??
          response?.data?.data?.pageInfo?.totalCount ??
          response?.pageInfo?.total ??
          response?.data?.pageInfo?.total ??
          response?.data?.data?.pageInfo?.total;

        const numericTotal = Number(rawTotal);
        const hasValidTotal =
          Number.isFinite(numericTotal) && numericTotal >= 0;

        const fallbackTotal =
          rolesData.length < itemsPerPage
            ? (currentPage - 1) * itemsPerPage + rolesData.length
            : currentPage * itemsPerPage + 1;

        const formattedRoles = rolesData.map((role, index) => {
          const roleId =
            role?._id || role?.jobId || `partner-role-${currentPage}-${index}`;

          const shortDescription = role?.shortDescription || "";
          const partnerIds = Array.isArray(role?.partnerIds)
            ? role.partnerIds.filter(Boolean).map(String)
            : [];

          const partnerId = String(
            role?.partnerId ||
              role?.sharedByPartnerId ||
              role?.partner?._id ||
              role?.partner?.id ||
              partnerIds[0] ||
              "",
          );

          const partnerName =
            role?.partnerName ||
            role?.partnerOrganization ||
            role?.partner?.name ||
            role?.sharedByPartnerName ||
            role?.organizationName ||
            role?.orgName ||
            role?.orgCode ||
            (partnerId ? `Partner ${partnerId}` : "Partner Shared");

          return {
            id: String(roleId),
            title: role?.roleName || "Untitled role",
            description: shortDescription,
            shortDescription,
            fullDescription:
              role?.fullDescription ||
              role?.longDescription ||
              shortDescription,
            department: role?.department || "",
            level: role?.level || "",
            openForHiring: role?.availability !== false,
            roleAvailability:
              role?.availability === false ? "Closed" : "Available",
            source: "Partner",
            partner: partnerName,
            partnerId,
            partnerIds,
            jobId: role?.jobId || "",
            mongoId: role?._id || "",
            tenantId: role?.tenantId || "",
            orgCode: role?.orgCode || "",
            visibility: role?.visibility || "",
            createdFrom: role?.createdFrom || "",
            createdAt: role?.createdAt || "",
            updatedAt: role?.updatedAt || "",
            skills: Array.isArray(role?.skills)
              ? role.skills
              : Array.isArray(role?.hardSkills)
                ? role.hardSkills
                : [],
            rawRole: role,
          };
        });

        setPartnerRoles(formattedRoles);
        setPartnerRolesTotal(hasValidTotal ? numericTotal : fallbackTotal);
      } catch (error) {
        // Allow the same request to be retried after a failure.
        partnerRolesRequestKeyRef.current = "";
        console.error("Error fetching partner shared roles:", error);
        setPartnerRoles([]);
        setPartnerRolesTotal(0);
        setPartnerRolesError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load partner-shared roles.",
        );
      } finally {
        setPartnerRolesLoading(false);
      }
    },
    [roleSearchTerm, roleDepartmentFilter, roleLevelFilter, rolePartnerFilter],
  );

  // API call to get organization job roles using server-side pagination.
  const handleRolesDataListApi = useCallback(
    async (currentPage = 1, itemsPerPage = ROLE_PAGE_SIZE) => {
      // Send only the filters that exist in the role-directory UI.
      const requestData = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        searchFilter: roleSearchTerm.trim(),
        department: roleDepartmentFilter,
        level: roleLevelFilter,
        hiringStatus: showOnlyOpenRoles ? "Open" : "All",
      };

      const requestKey = JSON.stringify(requestData);

      // Prevent duplicate requests, including React Strict Mode effect re-runs.
      if (organizationRolesRequestKeyRef.current === requestKey) {
        return;
      }

      organizationRolesRequestKeyRef.current = requestKey;

      try {
        setRolesLoading(true);
        setRolesError("");

        const response = await getAllJobRolesApi(requestData);

        // Support direct arrays and common service-wrapper response shapes.
        const rolesData = [
          response,
          response?.data,
          response?.data?.data,
          response?.result,
          response?.data?.result,
          response?.jobRoles,
          response?.data?.jobRoles,
          response?.content,
          response?.data?.content,
          response?.items,
          response?.data?.items,
          response?.data?.data?.content,
          response?.data?.data?.items,
          response?.data?.data?.jobRoles,
        ].find(Array.isArray);

        if (!rolesData) {
          throw new Error(
            "The job roles API returned an invalid response format.",
          );
        }

        const rawTotal =
          response?.totalCount ??
          response?.data?.totalCount ??
          response?.data?.data?.totalCount ??
          response?.totalElements ??
          response?.data?.totalElements ??
          response?.data?.data?.totalElements ??
          response?.totalItems ??
          response?.data?.totalItems ??
          response?.data?.data?.totalItems ??
          response?.totalRecords ??
          response?.data?.totalRecords ??
          response?.data?.data?.totalRecords ??
          response?.count ??
          response?.data?.count ??
          response?.data?.data?.count ??
          response?.total ??
          response?.data?.total ??
          response?.data?.data?.total ??
          response?.pagination?.totalCount ??
          response?.data?.pagination?.totalCount ??
          response?.data?.data?.pagination?.totalCount ??
          response?.pagination?.total ??
          response?.data?.pagination?.total ??
          response?.data?.data?.pagination?.total ??
          response?.pageInfo?.totalCount ??
          response?.data?.pageInfo?.totalCount ??
          response?.data?.data?.pageInfo?.totalCount ??
          response?.pageInfo?.total ??
          response?.data?.pageInfo?.total ??
          response?.data?.data?.pageInfo?.total;

        const numericTotal = Number(rawTotal);
        const hasValidTotal =
          Number.isFinite(numericTotal) && numericTotal >= 0;

        /*
         * A real total count from the API is preferred. The fallback keeps the
         * next page available when an older API response returns only an array.
         */
        const fallbackTotal =
          rolesData.length < itemsPerPage
            ? (currentPage - 1) * itemsPerPage + rolesData.length
            : currentPage * itemsPerPage + 1;

        const formattedRoles = rolesData.map((role, index) => {
          const roleId =
            role?._id ||
            role?.jobId ||
            `organization-role-${currentPage}-${index}`;

          const shortDescription = role?.shortDescription || "";

          return {
            // Normalize API fields to the model already used by the Hiring UI.
            id: String(roleId),
            title: role?.roleName || "Untitled role",
            description: shortDescription,
            shortDescription,
            fullDescription:
              role?.fullDescription ||
              role?.longDescription ||
              shortDescription,
            department: role?.department || "",
            level: role?.level || "",
            openForHiring: role?.availability !== false,
            roleAvailability:
              role?.availability === false ? "Closed" : "Available",
            source: "Internal",
            partner: null,
            jobId: role?.jobId || "",
            mongoId: role?._id || "",
            visibility: role?.visibility || "",
            createdFrom: role?.createdFrom || "",
            createdAt: role?.createdAt || "",
            updatedAt: role?.updatedAt || "",
            skills: Array.isArray(role?.skills) ? role.skills : [],
            rawRole: role,
          };
        });

        setOrganizationRoles(formattedRoles);
        setOrganizationRolesTotal(hasValidTotal ? numericTotal : fallbackTotal);
      } catch (error) {
        // Allow the same request to be retried after a failure.
        organizationRolesRequestKeyRef.current = "";
        console.error("Error fetching job roles:", error);
        setOrganizationRoles([]);
        setOrganizationRolesTotal(0);
        setRolesError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load job roles.",
        );
      } finally {
        setRolesLoading(false);
      }
    },
    [roleSearchTerm, roleDepartmentFilter, roleLevelFilter, showOnlyOpenRoles],
  );

  const allRoles = useMemo(
    () => [...organizationRoles, ...partnerRoles],
    [organizationRoles, partnerRoles],
  );

  // Load both role sources when the page opens. These effects intentionally do
  // not depend on activeRoleTab, so switching tabs only uses the cached state.
  useEffect(() => {
    handleRolesDataListApi(rolePageByTab.organization, roleItemsPerPage);
  }, [rolePageByTab.organization, roleItemsPerPage, handleRolesDataListApi]);

  useEffect(() => {
    handlePartnerSharedRolesApi(rolePageByTab.partner, roleItemsPerPage);
  }, [rolePageByTab.partner, roleItemsPerPage, handlePartnerSharedRolesApi]);

  useEffect(() => {
    getPartnerDataList();
  }, [getPartnerDataList]);

  useEffect(() => {
    getDepartmentsListApiHandler();
  }, [getDepartmentsListApiHandler]);

  useEffect(
    () => subscribePartnerCollaborationState(setPartnerCollaboration),
    [],
  );
  useEffect(() => subscribeHiringShortlists(setShortlists), []);

  useEffect(() => {
    if (!partnerCollaboration.enabled) {
      setShowHiringOnboarding(false);
      return;
    }

    try {
      const onboardingWasSeen =
        localStorage.getItem(getHiringOnboardingStorageKey()) === "true";

      setShowHiringOnboarding(!onboardingWasSeen);
    } catch (error) {
      console.warn("Unable to read Hiring onboarding state:", error);
    }
  }, [partnerCollaboration.enabled]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        partnerLookupRef.current &&
        !partnerLookupRef.current.contains(event.target)
      ) {
        setShowPartnerLookup(false);
      }

      if (
        matchPartnerLookupRef.current &&
        !matchPartnerLookupRef.current.contains(event.target)
      ) {
        setShowMatchPartnerLookup(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!descriptionRole) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setDescriptionRole(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [descriptionRole]);

  const showToast = (message, type = "success") => {
    MessageManager.showToast(type, message);
  };

  const closeHiringOnboarding = () => {
    try {
      localStorage.setItem(getHiringOnboardingStorageKey(), "true");
    } catch (error) {
      console.warn("Unable to save Hiring onboarding state:", error);
    }

    setShowHiringOnboarding(false);
    setHiringOnboardingStep(0);
  };

  const activeHiringOnboardingStep =
    HIRING_ONBOARDING_STEPS[hiringOnboardingStep];

  const HiringOnboardingIcon = activeHiringOnboardingStep.icon;
  const getRoleHiringStatus = (role) =>
    role.openForHiring === false ? "Closed" : "Open";

  const getRoleCardStatus = (role) =>
    role.openForHiring === false ? "Closed" : "Open";

  const roleDepartmentOptions = useMemo(
    () =>
      getUniqueMatchValues([
        ...departmentOptions,
        ...allRoles.map((role) => role.department),
        roleDepartmentFilter,
      ]).filter((department) => department !== "All"),
    [allRoles, departmentOptions, roleDepartmentFilter],
  );

  const roleLevelOptions = ROLE_LEVEL_FILTER_OPTIONS;

  // Partner filters must show only active partners that share Job Roles.
  const jobRoleSharingPartners = useMemo(
    () =>
      partners.filter((partner) => {
        const isActive =
          String(partner?.status || "")
            .trim()
            .toUpperCase() === "ACTIVE";

        const sharesJobRoles = (partner?.sharedData || []).some(
          (sharedItem) =>
            String(sharedItem || "")
              .trim()
              .toLowerCase()
              .replace(/[\s_-]+/g, "") === "jobroles",
        );

        return isActive && sharesJobRoles;
      }),
    [partners],
  );

  const rolePartnerOptions = useMemo(
    () =>
      jobRoleSharingPartners.map((partner) => ({
        id: partner.id,
        label: partner.partnerOrganization,
        status: partner.status,
        partnerOrgCode: partner.partnerOrgCode,
        partnerTenantId: partner.partnerTenantId,
        partnerOrganization: partner.partnerOrganization,
        sharedData: partner.sharedData,
      })),
    [jobRoleSharingPartners],
  );

  const visiblePartnerLookupOptions = useMemo(() => {
    const searchValue = rolePartnerLookup.trim().toLowerCase();

    return rolePartnerOptions.filter((option) =>
      `${option.label} ${option.id}`.toLowerCase().includes(searchValue),
    );
  }, [rolePartnerLookup, rolePartnerOptions]);

  const rolesForActiveTab = useMemo(
    () => (activeRoleTab === "organization" ? organizationRoles : partnerRoles),
    [activeRoleTab, organizationRoles, partnerRoles],
  );

  const activeRolePage = rolePageByTab[activeRoleTab];

  // Both role endpoints return the current server-side page.
  const paginatedRoles = rolesForActiveTab;

  const rolePaginationTotal =
    activeRoleTab === "organization"
      ? organizationRolesTotal
      : partnerRolesTotal;

  const activeRolesLoading =
    activeRoleTab === "organization" ? rolesLoading : partnerRolesLoading;

  const activeRolesError =
    activeRoleTab === "organization" ? rolesError : partnerRolesError;

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(rolePaginationTotal / roleItemsPerPage),
    );

    if (activeRolePage > maxPage) {
      setRolePageByTab((previous) => ({
        ...previous,
        [activeRoleTab]: maxPage,
      }));
    }
  }, [activeRolePage, activeRoleTab, rolePaginationTotal, roleItemsPerPage]);

  const selectedRole = useMemo(
    () => allRoles.find((role) => role.id === selectedRoleId) || null,
    [allRoles, selectedRoleId],
  );

  const partnerOptions = useMemo(
    () =>
      Array.from(
        new Set(
          partners
            .filter(
              (partner) =>
                String(partner?.status || "").toUpperCase() === "ACTIVE",
            )
            .map((partner) => partner.partnerOrganization)
            .filter(Boolean),
        ),
      ),
    [partners],
  );

  const visibleMatchPartnerOptions = useMemo(() => {
    const searchValue = matchPartnerLookup.trim().toLowerCase();

    return partnerOptions.filter((partner) =>
      partner.toLowerCase().includes(searchValue),
    );
  }, [matchPartnerLookup, partnerOptions]);

  const getProfileSourceLabel = (profile) => profile.source || "Unknown";

  const getMatchSourceLabel = (profile) => profile.source || "Unknown";

  const availableMatchSourceOptions = useMemo(
    () =>
      getUniqueMatchValues([
        ...MATCH_SOURCE_FILTER_OPTIONS,
        ...matchSourceValues,
      ]),
    [matchSourceValues],
  );

  const availableMatchAvailabilityOptions = useMemo(
    () =>
      getUniqueMatchValues([
        ...MATCH_AVAILABILITY_FILTER_OPTIONS,
        ...matchAvailabilityValues,
      ]).filter((availability) => availability !== "Unknown"),
    [matchAvailabilityValues],
  );

  const isMatchProfileSaved = (profile) => {
    const apiShortlistFlag = getProfileShortlistFlag(profile);

    if (typeof apiShortlistFlag === "boolean") {
      return apiShortlistFlag;
    }

    return false;
  };

  const getMatchScoreClass = (score) => {
    const numericScore = Number(score) || 0;

    if (numericScore >= 90) return "excellent";
    if (numericScore >= 80) return "strong";
    if (numericScore >= 70) return "moderate";

    return "low";
  };
  const handleHiringViewChange = (nextView) => {
    setHiringView(nextView);
    if (nextView === "shortlist") {
      void getShortlistedJobRolesApiHandler();
    } else {
      shortlistOverviewRequestKeyRef.current = "";
    }
    sessionStorage.setItem(HIRING_ACTIVE_VIEW_KEY, nextView);
  };
  const getShortlistedJobRolesApiHandler = useCallback(
    async (forceRefresh = false, requestOverrides = {}) => {
      const requestData = {
        search: shortlistOverviewSearchTerm.trim(),
        department:
          shortlistOverviewDepartment === "All"
            ? ""
            : shortlistOverviewDepartment,
        level: shortlistOverviewLevel === "All" ? "" : shortlistOverviewLevel,
        pageNo: shortlistOverviewCurrentPage,
        pageSize: shortlistOverviewItemsPerPage,
        ...requestOverrides,
      };

      const requestKey = JSON.stringify(requestData);

      if (
        !forceRefresh &&
        shortlistOverviewRequestKeyRef.current === requestKey
      ) {
        return;
      }

      shortlistOverviewRequestKeyRef.current = requestKey;

      try {
        setShortlistOverviewLoading(true);
        setShortlistOverviewError("");

        const response = await getShortlistedJobRolesApi(requestData);

        if (Number(response?.code) !== 200) {
          throw new Error(
            response?.message || "Unable to load shortlisted job roles.",
          );
        }

        const rolesData = getShortlistedJobRolesFromResponse(response);
        const normalizedRoles = rolesData.map(
          normalizeShortlistedJobRoleSummary,
        );

        setShortlistOverviewRoles(normalizedRoles);
        setShortlistOverviewTotal(
          getShortlistedJobRolesTotalFromResponse(
            response,
            normalizedRoles.length,
          ),
        );
        setShortlistOverviewLoaded(true);
      } catch (error) {
        console.error("Error fetching shortlisted job roles:", error);
        shortlistOverviewRequestKeyRef.current = "";
        setShortlistOverviewRoles([]);
        setShortlistOverviewTotal(0);
        setShortlistOverviewError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load shortlisted job roles.",
        );
      } finally {
        setShortlistOverviewLoading(false);
      }
    },
    [
      shortlistOverviewSearchTerm,
      shortlistOverviewDepartment,
      shortlistOverviewLevel,
      shortlistOverviewCurrentPage,
      shortlistOverviewItemsPerPage,
    ],
  );

  useEffect(() => {
    getShortlistedJobRolesApiHandler();
  }, [hiringView, getShortlistedJobRolesApiHandler]);

  const shortlistOverviewFallbackTotal = useMemo(
    () => Object.keys(shortlists || {}).length,
    [shortlists],
  );

  const shortlistOverviewBadgeTotal = shortlistOverviewLoaded
    ? shortlistOverviewTotal
    : shortlistOverviewFallbackTotal;

  const shortlistGroups = useMemo(
    () => shortlistOverviewRoles.filter(Boolean),
    [shortlistOverviewRoles],
  );

  const shortlistOverviewDepartments = useMemo(
    () =>
      getUniqueMatchValues([
        ...departmentOptions,
        ...shortlistOverviewRoles
          .map((shortlist) => shortlist?.role?.department)
          .filter(Boolean),
        shortlistOverviewDepartment,
      ]).filter((department) => department !== "All"),
    [departmentOptions, shortlistOverviewDepartment, shortlistOverviewRoles],
  );

  const shortlistOverviewLevels = ROLE_LEVEL_FILTER_OPTIONS;

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(shortlistOverviewTotal / shortlistOverviewItemsPerPage),
    );

    if (shortlistOverviewCurrentPage > maxPage) {
      setShortlistOverviewCurrentPage(maxPage);
    }
  }, [
    shortlistOverviewTotal,
    shortlistOverviewCurrentPage,
    shortlistOverviewItemsPerPage,
  ]);

  const getShortlistOverviewFilterCount = () =>
    [
      Boolean(shortlistOverviewSearchTerm),
      shortlistOverviewDepartment !== "All",
      shortlistOverviewLevel !== "All",
    ].filter(Boolean).length;

  const clearShortlistOverviewFilters = () => {
    setShortlistOverviewSearchInput("");
    setShortlistOverviewSearchTerm("");
    setShortlistOverviewDepartment("All");
    setShortlistOverviewLevel("All");
    setShowShortlistOverviewSearch(false);
    setShowShortlistOverviewFilters(false);
    setShortlistOverviewCurrentPage(1);
  };
  const selectedShortlistOverviewRole = useMemo(
    () =>
      shortlistOverviewRoles.find(
        (shortlist) =>
          shortlist.id === selectedShortlistRoleId ||
          shortlist.jobId === selectedShortlistRoleId ||
          shortlist.shortlistId === selectedShortlistRoleId,
      ) || null,
    [shortlistOverviewRoles, selectedShortlistRoleId],
  );
  const selectedShortlistRole =
    selectedShortlistRoleSnapshot || selectedShortlistOverviewRole;
  const selectedShortlist = selectedShortlistApiState || selectedShortlistRole;

  const shortlistPartnerOptions = useMemo(
    () =>
      jobRoleSharingPartners.map((partner) => ({
        id: partner.id,
        label: partner.partnerOrganization,
        status: partner.status,
        partnerOrgCode: partner.partnerOrgCode,
        partnerTenantId: partner.partnerTenantId,
        partnerOrganization: partner.partnerOrganization,
        sharedData: partner.sharedData,
      })),
    [jobRoleSharingPartners],
  );
  const openShortlistedJobRoleApiHandler = useCallback(
    async (roleOverride = null) => {
      const role = roleOverride || selectedShortlistRole?.role || null;
      const jobId = String(role?.jobId || "").trim();

      if (!jobId) {
        setSelectedShortlistApiState(null);
        setShortlistProfiles([]);
        setShortlistTotal(0);
        setShortlistError(
          "The selected shortlist role does not have a valid job ID.",
        );
        return;
      }

      const selectedPartner =
        shortlistPartnerFilter === "All"
          ? null
          : partners.find(
              (partner) =>
                String(partner.id) === String(shortlistPartnerFilter),
            );

      try {
        setShortlistLoading(true);
        setShortlistError("");
        setShortlistProfiles([]);
        setShortlistTotal(0);

        const requestData = {
          jobId,
          source: shortlistSourceFilter === "All" ? "" : shortlistSourceFilter,
          partnerId: selectedPartner?.id || "",
          search: shortlistSearchTerm.trim(),
          availability: getMatchAvailabilityRequestValue(
            shortlistAvailabilityFilter,
          ),
          matchPer: "",
          pageNo: shortlistCurrentPage,
          pageSize: shortlistItemsPerPage,
        };

        const response = await openShortlistedJobRoleApi(requestData);
        const normalizedShortlist = normalizeOpenShortlistResponse(response, {
          ...(role || {}),
          name: selectedShortlistNameRef.current || role?.name || "",
        });

        setSelectedShortlistApiState(normalizedShortlist);
        selectedShortlistNameRef.current = normalizedShortlist.name;
        setShortlistProfiles(normalizedShortlist.profiles);
        setShortlistTotal(normalizedShortlist.totalCount);
        setShortlistNameInput(normalizedShortlist.name);
        setMatchSourceValues((previous) =>
          getUniqueMatchValues([
            ...previous,
            ...normalizedShortlist.profiles.map((profile) => profile.source),
          ]),
        );
        setMatchAvailabilityValues((previous) =>
          getUniqueMatchValues([
            ...previous,
            ...normalizedShortlist.profiles.map(
              (profile) => profile.rawProfile?.availability,
            ),
          ]),
        );
      } catch (error) {
        console.error("Failed to fetch shortlisted profiles:", error);
        setSelectedShortlistApiState(null);
        setShortlistProfiles([]);
        setShortlistTotal(0);
        setShortlistError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load shortlisted profiles.",
        );
      } finally {
        setShortlistLoading(false);
      }
    },
    [
      selectedShortlistRole?.role,
      selectedShortlistRoleId,
      shortlistSourceFilter,
      shortlistSearchTerm,
      shortlistAvailabilityFilter,
      shortlistPartnerFilter,
      shortlistCurrentPage,
      shortlistItemsPerPage,
      partners,
    ],
  );

  useEffect(() => {
    if (
      hiringView !== "shortlist" ||
      !selectedShortlistRoleId ||
      !selectedShortlistRole?.role?.jobId
    ) {
      return;
    }

    openShortlistedJobRoleApiHandler();
  }, [
    hiringView,
    selectedShortlistRoleId,
    selectedShortlistRole?.role?.jobId,
    openShortlistedJobRoleApiHandler,
  ]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(shortlistTotal / shortlistItemsPerPage),
    );

    if (shortlistCurrentPage > maxPage) {
      setShortlistCurrentPage(maxPage);
    }
  }, [shortlistTotal, shortlistCurrentPage, shortlistItemsPerPage]);

  const currentShortlistPageItems = shortlistProfiles;
  const currentShortlistPageIds = currentShortlistPageItems.map(
    (profile) => profile.id,
  );

  const allShortlistProfilesSelected =
    shortlistTotal > 0 && shortlistSelectedIds.length >= shortlistTotal;

  const someShortlistProfilesSelected =
    shortlistSelectedIds.length > 0 && !allShortlistProfilesSelected;
  const getAppliedShortlistFiltersCount = () =>
    [
      shortlistSearchTerm,
      shortlistSourceFilter !== "All",
      shortlistAvailabilityFilter !== "All",
      shortlistPartnerFilter !== "All",
    ].filter(Boolean).length;
  const clearShortlistFilters = () => {
    setShortlistSearchTerm("");
    setShortlistSearchInput("");
    setShortlistSourceFilter("All");
    setShortlistAvailabilityFilter("All");
    setShortlistPartnerFilter("All");
    setShowShortlistSearch(false);
    setShowShortlistFilters(false);
    setShortlistCurrentPage(1);
  };

  const handleSelectShortlistProfile = (profileId, checked) => {
    const selectedProfile = shortlistProfiles.find(
      (profile) => profile.id === profileId,
    );

    setShortlistSelectedIds((previous) =>
      checked
        ? Array.from(new Set([...previous, profileId]))
        : previous.filter((id) => id !== profileId),
    );

    setShortlistSelectedProfiles((previous) => {
      if (!checked) {
        return previous.filter((profile) => profile.id !== profileId);
      }

      if (!selectedProfile) return previous;

      return [
        ...previous.filter((profile) => profile.id !== profileId),
        selectedProfile,
      ];
    });
  };

  useEffect(() => {
    setShortlistSelectedIds([]);
    setShortlistSelectedProfiles([]);
  }, [
    selectedShortlistRoleId,
    shortlistSearchTerm,
    shortlistSourceFilter,
    shortlistAvailabilityFilter,
    shortlistPartnerFilter,
  ]);

  const handleSelectAllShortlistProfiles = async (checked) => {
    if (!checked) {
      setShortlistSelectedIds([]);
      setShortlistSelectedProfiles([]);
      return;
    }

    const role = selectedShortlist?.role;

    if (!role?.jobId) {
      showToast("Unable to select profiles because the role has no job ID.");
      return;
    }

    const selectedPartner =
      shortlistPartnerFilter === "All"
        ? null
        : partners.find(
            (partner) => String(partner.id) === String(shortlistPartnerFilter),
          );

    try {
      setShortlistSelectAllLoading(true);

      // Intentionally omit pageNo and pageSize. This request must return every
      // shortlisted profile matching the currently applied filters.
      const requestData = {
        jobId: role.jobId,
        source: shortlistSourceFilter === "All" ? "" : shortlistSourceFilter,
        search: shortlistSearchTerm.trim(),
        availability: getMatchAvailabilityRequestValue(
          shortlistAvailabilityFilter,
        ),
        matchPer: "",
        partnerId: selectedPartner?.id || "",
      };

      const response = await openShortlistedJobRoleApi(requestData);
      const profilesData = getOpenShortlistProfilesFromResponse(response);

      if (!profilesData) {
        throw new Error(
          "The select-all API returned an invalid response format.",
        );
      }

      const allProfiles = Array.from(
        new Map(
          profilesData
            .map(normalizeMatchProfile)
            .map((profile) => [profile.id, profile]),
        ).values(),
      );
      const allProfileIds = allProfiles.map((profile) => profile.id);

      setMatchSourceValues((previous) =>
        getUniqueMatchValues([
          ...previous,
          ...profilesData.map((profile) => profile?.source),
        ]),
      );
      setMatchAvailabilityValues((previous) =>
        getUniqueMatchValues([
          ...previous,
          ...profilesData.map((profile) => profile?.availability),
        ]),
      );
      setShortlistSelectedIds(allProfileIds);
      setShortlistSelectedProfiles(allProfiles);
      setShortlistTotal(allProfileIds.length);

      showToast(
        `${allProfileIds.length} shortlisted profile${
          allProfileIds.length === 1 ? "" : "s"
        } selected.`,
      );
    } catch (error) {
      console.error("Failed to select all shortlisted profiles:", error);
      setShortlistSelectedIds([]);
      setShortlistSelectedProfiles([]);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to select all shortlisted profiles.",
      );
    } finally {
      setShortlistSelectAllLoading(false);
    }
  };

  const openShortlist = (shortlistOrId) => {
    const shortlist =
      typeof shortlistOrId === "string"
        ? shortlistOverviewRoles.find(
            (item) =>
              item.id === shortlistOrId ||
              item.jobId === shortlistOrId ||
              item.shortlistId === shortlistOrId,
          ) || null
        : shortlistOrId;

    if (!shortlist) {
      return;
    }

    setSelectedShortlistApiState(null);
    setSelectedShortlistRoleSnapshot(shortlist);
    selectedShortlistNameRef.current = shortlist?.name || "";
    setSelectedShortlistRoleId(shortlist.id);
    setRenamingShortlist(false);
    setShortlistSelectedIds([]);
    setShortlistSelectedProfiles([]);
    setShortlistCurrentPage(1);
    clearShortlistFilters();

    sessionStorage.setItem(HIRING_OPEN_SHORTLIST_KEY, shortlist.id);
  };

  const closeShortlist = () => {
    setSelectedShortlistRoleId("");
    setSelectedShortlistApiState(null);
    setSelectedShortlistRoleSnapshot(null);
    selectedShortlistNameRef.current = "";
    setShortlistProfiles([]);
    setShortlistTotal(0);
    setShortlistError("");
    setShortlistSelectedIds([]);
    setShortlistSelectedProfiles([]);
    setShortlistCurrentPage(1);
    setShortlistNameInput("");
    setRenamingShortlist(false);
    clearShortlistFilters();

    sessionStorage.removeItem(HIRING_OPEN_SHORTLIST_KEY);
  };

  const handleToggleShortlist = async (event, profile) => {
    event.stopPropagation();
    if (!activeRole?.jobId) {
      showToast("Unable to update shortlist because the role has no job ID.");
      return;
    }

    const userId = getMatchProfileUserId(profile);
    if (!userId) {
      showToast(
        "Unable to update shortlist because the profile has no user ID.",
      );
      return;
    }

    const shouldRemove = isMatchProfileSaved(profile);

    try {
      setShortlistActionProfileId(profile.id);

      const requestData = {
        jobId: activeRole.jobId,
        userIdArray: [userId],
      };

      if (shouldRemove) {
        ensureShortlistApiSuccess(
          await removePersonFromShortlistApi(requestData),
        );
      } else {
        ensureShortlistApiSuccess(
          await shortListPersonForJobRoleApi(requestData),
        );
      }

      const nextProfile = { ...profile, saved: !shouldRemove };
      const nextState = shouldRemove
        ? removeProfilesFromRoleShortlist(activeRole.id, [profile.id])
        : addProfilesToRoleShortlist(activeRole, [nextProfile]);

      setShortlists(nextState);
      setMatches((previous) =>
        previous.map((item) => (item.id === profile.id ? nextProfile : item)),
      );
      setSelectedMatchProfiles((previous) =>
        previous.map((item) => (item.id === profile.id ? nextProfile : item)),
      );
      setSelectedShortlistRoleId(activeRole.jobId || activeRole.id);
      void getShortlistedJobRolesApiHandler(true);
      showToast(
        shouldRemove ? "Removed from shortlist" : "Added to shortlist",
      );
    } catch (error) {
      console.error("Failed to update shortlist:", error);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update shortlist.",
      );
    } finally {
      setShortlistActionProfileId("");
    }
  };

  const handleRemoveShortlistedProfiles = async (profileIds) => {
    const role = selectedShortlist?.role;
    if (!role?.jobId || !profileIds.length) return;

    const profilesById = new Map(
      [...shortlistProfiles, ...shortlistSelectedProfiles].map((profile) => [
        profile.id,
        profile,
      ]),
    );
    const profilesToRemove = profileIds
      .map((profileId) => profilesById.get(profileId))
      .filter(Boolean);
    const missingUserId = profilesToRemove.some(
      (profile) => !getMatchProfileUserId(profile),
    );

    if (profilesToRemove.length !== profileIds.length || missingUserId) {
      showToast("Unable to remove one or more profiles without a user ID.");
      return;
    }

    try {
      setShortlistBulkActionLoading(true);
      await ensureShortlistApiSuccess(
        await removePersonFromShortlistApi({
          jobId: role.jobId,
          userIdArray: profilesToRemove.map((profile) =>
            getMatchProfileUserId(profile),
          ),
        }),
      );

      const nextState = removeProfilesFromRoleShortlist(role.id, profileIds);
      setShortlists(nextState);
      setShortlistProfiles((previous) =>
        previous.filter((profile) => !profileIds.includes(profile.id)),
      );
      setShortlistTotal((previous) =>
        Math.max(0, previous - profileIds.length),
      );
      setShortlistSelectedIds([]);
      setShortlistSelectedProfiles([]);
      void getShortlistedJobRolesApiHandler(true);
      showToast("Removed from shortlist");
    } catch (error) {
      console.error("Failed to remove profiles from shortlist:", error);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to remove profiles from shortlist.",
      );
    } finally {
      setShortlistBulkActionLoading(false);
    }
  };
  const handleSaveShortlistName = () => {
    const role = selectedShortlist?.role;
    const nextName = String(shortlistNameInput || "").trim();

    if (!role?.jobId || !nextName) {
      showToast("Unable to update shortlist name.");
      return;
    }

    const requestData = {
      jobId: role.jobId,
      shortlistName: nextName,
    };

    updateShortlistNameApi(requestData)
      .then((response) => {
        if (Number(response?.code) !== 200) {
          throw new Error(
            response?.message || "Unable to update shortlist name.",
          );
        }

        const nextState = renameRoleShortlist(role.id, nextName);
        setShortlists(nextState);
        const updatedAt = new Date().toISOString();

        const updateSelectedShortlistState = (previous) =>
          previous
            ? {
                ...previous,
                name: nextName,
                updatedAt,
                shortlist: {
                  ...previous.shortlist,
                  shortlistName: nextName,
                  updatedDate: updatedAt,
                },
              }
            : previous;

        setSelectedShortlistApiState(updateSelectedShortlistState);
        setSelectedShortlistRoleSnapshot(updateSelectedShortlistState);
        setShortlistOverviewRoles((previous) =>
          previous.map((shortlist) =>
            shortlist.id === role.id || shortlist.jobId === role.jobId
              ? {
                  ...shortlist,
                  name: nextName,
                  updatedAt,
                  rawShortlist: {
                    ...shortlist.rawShortlist,
                    shortlistName: nextName,
                    updatedDate: updatedAt,
                  },
                }
              : shortlist,
          ),
        );

        selectedShortlistNameRef.current = nextName;
        setRenamingShortlist(false);
        showToast("Shortlist renamed");
      })
      .catch((error) => {
        console.error("Failed to update shortlist name:", error);
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to update shortlist name.",
        );
      });
  };
  const filteredSortedMatches = useMemo(() => {
    if (!activeRole) return [];

    return [...matches].sort((first, second) => {
      let firstValue;
      let secondValue;

      if (matchSort.field === "profile") {
        firstValue = String(first.name || "").toLowerCase();
        secondValue = String(second.name || "").toLowerCase();
      } else if (matchSort.field === "availability") {
        firstValue = String(first.availability || "Unknown").toLowerCase();
        secondValue = String(second.availability || "Unknown").toLowerCase();
      } else {
        firstValue = Number(first.match) || 0;
        secondValue = Number(second.match) || 0;
      }

      if (firstValue === secondValue) return 0;
      const comparison = firstValue > secondValue ? 1 : -1;
      return matchSort.direction === "asc" ? comparison : -comparison;
    });
  }, [activeRole, matches, matchSort]);

  // The API already returns exactly one server-side page.
  const currentMatchPageItems = filteredSortedMatches;
  const selectedMatchIdSet = useMemo(
    () => new Set(selectedMatchIds),
    [selectedMatchIds],
  );
  const allMatchProfilesSelected =
    matchesTotal > 0 && selectedMatchIdSet.size >= matchesTotal;

  const someMatchProfilesSelected =
    selectedMatchIdSet.size > 0 && !allMatchProfilesSelected;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(matchesTotal / matchItemsPerPage));

    if (matchCurrentPage > maxPage) {
      setMatchCurrentPage(maxPage);
    }
  }, [matchesTotal, matchCurrentPage, matchItemsPerPage]);

  const getAppliedMatchFiltersCount = () => {
    let count = 0;
    if (matchSearchTerm) count += 1;
    if (filterSource !== "All") count += 1;
    if (filterPartner !== "All") count += 1;
    if (filterAvailability !== "All") count += 1;
    if (filterMatch !== "All") count += 1;
    if (showSavedOnly) count += 1;
    return count;
  };

  const getAppliedRoleFiltersCount = () => {
    let count = 0;
    if (roleSearchTerm) count += 1;
    if (roleDepartmentFilter !== "All") count += 1;
    if (roleLevelFilter !== "All") count += 1;
    if (activeRoleTab === "partner" && rolePartnerFilter !== "All") count += 1;
    if (activeRoleTab === "organization" && showOnlyOpenRoles) {
      count += 1;
    }
    return count;
  };

  const handleRoleSearchButtonClick = () => {
    setRoleSearchTerm(roleSearchInputValue.trim());
    setRolePageByTab((previous) => ({
      ...previous,
      [activeRoleTab]: 1,
    }));
    setShowRoleSearchBar(false);
  };

  const handleClearRoleSearch = () => {
    setRoleSearchInputValue("");
    setRoleSearchTerm("");
    setRolePageByTab((previous) => ({
      ...previous,
      [activeRoleTab]: 1,
    }));
  };

  const clearRoleFilters = () => {
    setRoleSearchInputValue("");
    setRoleSearchTerm("");
    setRoleDepartmentFilter("All");
    setRoleLevelFilter("All");
    setRolePartnerFilter("All");
    setRolePartnerLookup("");
    setShowPartnerLookup(false);
    setShowOnlyOpenRoles(false);
    setShowRoleSearchBar(false);
    setShowRoleFiltersPanel(false);
    setRolePageByTab({ organization: 1, partner: 1 });
  };

  const clearMatchFilters = () => {
    setSelectedMatchIds([]);
    setSelectedMatchProfiles([]);
    setMatchSourceValues([]);
    setMatchAvailabilityValues([]);
    setMatchSearchInputValue("");
    setMatchSearchTerm("");
    setFilterSource("All");
    setFilterPartner("All");
    setMatchPartnerLookup("");
    setShowMatchPartnerLookup(false);
    setFilterAvailability("All");
    setFilterMatch("All");
    setShowSavedOnly(false);
    setShowMatchSearchBar(false);
    setShowMatchFiltersPanel(false);
    setMatchCurrentPage(1);
  };

  const handleRoleTabChange = (tab) => {
    // Tab changes only switch the visible cached result set. They must not
    // mutate API filter state or trigger either role endpoint again.
    setActiveRoleTab(tab);
    setSelectedRoleId("");
    setShowPartnerLookup(false);
    setShowRoleSearchBar(false);
    setShowRoleFiltersPanel(false);
  };

  const handleFindMatches = () => {
    if (!selectedRole) return;

    setActiveRole(selectedRole);
    setSelectedMatchIds([]);
    setSelectedMatchProfiles([]);
    clearMatchFilters();
  };

  const handleFindMatchesApi = useCallback(
    async (role = activeRole) => {
      if (!role?.jobId) {
        setMatches([]);
        setMatchesTotal(0);
        setMatchesError("The selected role does not have a valid job ID.");
        return;
      }

      const selectedPartner =
        filterPartner === "All"
          ? null
          : partners.find(
              (partner) =>
                partner.partnerOrganization === filterPartner ||
                String(partner.id) === String(filterPartner),
            );

      try {
        setMatchesLoading(true);
        setMatchesError("");

        const requestData = {
          jobId: role.jobId,
          source: filterSource === "All" ? "" : filterSource,
          search: matchSearchTerm.trim(),
          availability: getMatchAvailabilityRequestValue(filterAvailability),
          matchPer: filterMatch === "All" ? "" : filterMatch,
          isShortlisted: showSavedOnly,
          pageNo: matchCurrentPage,
          pageSize: matchItemsPerPage,
          partnerId: selectedPartner?.id || "",
          partnerTenantId: selectedPartner?.partnerTenantId || "",
        };

        const response = await getMatchesForjobRoleApi(requestData);
        const matchesData = getMatchesArrayFromResponse(response);

        if (!matchesData) {
          throw new Error(
            "The matches API returned an invalid response format.",
          );
        }

        const pageMatchesData =
          matchesData.length > matchItemsPerPage
            ? matchesData.slice(
                (matchCurrentPage - 1) * matchItemsPerPage,
                matchCurrentPage * matchItemsPerPage,
              )
            : matchesData;

        const normalizedMatches = pageMatchesData.map(normalizeMatchProfile);
        setMatchSourceValues((previous) =>
          getUniqueMatchValues([
            ...previous,
            ...normalizedMatches.map((profile) => profile.source),
          ]),
        );
        setMatchAvailabilityValues((previous) =>
          getUniqueMatchValues([
            ...previous,
            ...normalizedMatches.map(
              (profile) => profile.rawProfile?.availability,
            ),
          ]),
        );
        const total =
          matchesData.length > matchItemsPerPage
            ? matchesData.length
            : getMatchesTotalFromResponse(
                response,
                matchCurrentPage,
                matchItemsPerPage,
                normalizedMatches.length,
              );

        setMatches(normalizedMatches);
        setMatchesTotal(total);
      } catch (error) {
        console.error("Failed to fetch matches for job role:", error);
        setMatches([]);
        setMatchesTotal(0);
        setMatchesError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load matching profiles.",
        );
      } finally {
        setMatchesLoading(false);
      }
    },
    [
      activeRole,
      filterSource,
      filterPartner,
      filterAvailability,
      filterMatch,
      matchSearchTerm,
      showSavedOnly,
      matchCurrentPage,
      matchItemsPerPage,
      partners,
    ],
  );

  useEffect(() => {
    if (hiringView !== "matches" || !activeRole?.jobId) {
      return;
    }

    handleFindMatchesApi(activeRole);
  }, [hiringView, activeRole?.jobId, handleFindMatchesApi]);

  const handleChangeRole = () => {
    sessionStorage.removeItem(HIRING_ACTIVE_ROLE_KEY);
    sessionStorage.removeItem("mmhHiringProfileDetail");

    setActiveRole(null);
    setSelectedRoleId("");
    setSelectedMatchIds([]);
    setSelectedMatchProfiles([]);
    setMatches([]);
    setMatchesTotal(0);
    setMatchesError("");
    clearMatchFilters();
  };

  const handleSort = (field) => {
    setMatchSort((previous) => ({
      field,
      direction:
        previous.field === field && previous.direction === "asc"
          ? "desc"
          : "asc",
    }));
    setMatchCurrentPage(1);
  };

  const renderSortChevrons = (field) => (
    <span className="mmh-list-sort-chevrons" aria-hidden="true">
      <ChevronUp
        size={13}
        className={
          matchSort.field === field && matchSort.direction === "asc"
            ? "active"
            : ""
        }
      />
      <ChevronDown
        size={13}
        className={
          matchSort.field === field && matchSort.direction === "desc"
            ? "active"
            : ""
        }
      />
    </span>
  );

  const handleSelectMatch = (profileId, checked) => {
    const selectedProfile = matches.find((profile) => profile.id === profileId);

    setSelectedMatchIds((previous) =>
      checked
        ? Array.from(new Set([...previous, profileId]))
        : previous.filter((id) => id !== profileId),
    );

    setSelectedMatchProfiles((previous) => {
      if (!checked) {
        return previous.filter((profile) => profile.id !== profileId);
      }

      if (!selectedProfile) return previous;

      return [
        ...previous.filter((profile) => profile.id !== selectedProfile.id),
        selectedProfile,
      ];
    });
  };

  useEffect(() => {
    setSelectedMatchIds([]);
    setSelectedMatchProfiles([]);
  }, [
    activeRole?.jobId,
    matchSearchTerm,
    filterSource,
    filterPartner,
    filterAvailability,
    filterMatch,
    showSavedOnly,
  ]);

  const handleSelectAllMatchProfiles = async (checked) => {
    if (!checked) {
      setSelectedMatchIds([]);
      setSelectedMatchProfiles([]);
      return;
    }

    if (!activeRole?.jobId) {
      showToast("Unable to select profiles because the role has no job ID.");
      return;
    }

    const selectedPartner =
      filterPartner === "All"
        ? null
        : partners.find(
            (partner) =>
              partner.partnerOrganization === filterPartner ||
              String(partner.id) === String(filterPartner),
          );

    try {
      setMatchSelectAllLoading(true);

      // pageNo and pageSize are intentionally omitted so the API returns every
      // profile matching the active role and the currently applied filters.
      const requestData = {
        jobId: activeRole.jobId,
        source: filterSource === "All" ? "" : filterSource,
        search: matchSearchTerm.trim(),
        availability: getMatchAvailabilityRequestValue(filterAvailability),
        matchPer: filterMatch === "All" ? "" : filterMatch,
        isShortlisted: showSavedOnly,
        partnerId: selectedPartner?.id || "",
        partnerTenantId: selectedPartner?.partnerTenantId || "",
      };

      const response = await getMatchesForjobRoleApi(requestData);
      const profilesData = getMatchesArrayFromResponse(response);

      if (!profilesData) {
        throw new Error(
          "The select-all API returned an invalid response format.",
        );
      }

      const allProfiles = profilesData.map(normalizeMatchProfile);
      setMatchSourceValues((previous) =>
        getUniqueMatchValues([
          ...previous,
          ...allProfiles.map((profile) => profile.source),
        ]),
      );
      setMatchAvailabilityValues((previous) =>
        getUniqueMatchValues([
          ...previous,
          ...allProfiles.map((profile) => profile.rawProfile?.availability),
        ]),
      );
      const uniqueProfiles = Array.from(
        new Map(allProfiles.map((profile) => [profile.id, profile])).values(),
      );
      const allProfileIds = uniqueProfiles.map((profile) => profile.id);

      setSelectedMatchIds(allProfileIds);
      setSelectedMatchProfiles(uniqueProfiles);

      // Keep the visible total aligned with the unpaginated response when the
      // endpoint returns a direct array without a separate count field.
      if (allProfileIds.length > matchesTotal) {
        setMatchesTotal(allProfileIds.length);
      }

      showToast(
        `${allProfileIds.length} profile${
          allProfileIds.length === 1 ? "" : "s"
        } selected.`,
      );
    } catch (error) {
      console.error("Failed to select all matching profiles:", error);
      setSelectedMatchIds([]);
      setSelectedMatchProfiles([]);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to select all matching profiles.",
      );
    } finally {
      setMatchSelectAllLoading(false);
    }
  };

  const handleBulkNotifyInterest = async () => {
    if (!activeRole?.jobId || !selectedMatchIds.length) return;

    const selectedPartnerProfiles = selectedMatchProfiles.filter(
      (profile) => !profile.isInternal,
    );

    if (!selectedPartnerProfiles.length) {
      showToast("Notify Interest applies only to Partner Shared profiles.");
      return;
    }

    const hasMissingData = selectedPartnerProfiles.some(
      (profile) =>
        !String(
          profile?.rawProfile?.partnerId || profile?.partnerId || "",
        ).trim() || !getMatchProfileUserId(profile),
    );

    if (hasMissingData) {
      showToast(
        "Unable to notify interest because partner or profile information is missing.",
      );
      return;
    }

    const profilesByPartner = new Map();

    selectedPartnerProfiles.forEach((profile) => {
      const partnerId = String(
        profile?.rawProfile?.partnerId || profile?.partnerId || "",
      ).trim();

      const userId = getMatchProfileUserId(profile);

      if (!profilesByPartner.has(partnerId)) {
        profilesByPartner.set(partnerId, []);
      }

      profilesByPartner.get(partnerId).push(userId);
    });

    try {
      setShortlistBulkActionLoading(true);

      const responses = await Promise.all(
        Array.from(profilesByPartner.entries()).map(
          ([partnerId, userIdArray]) =>
            notifyInterestApi({
              partnerId,
              userIdArray,
              jobId: activeRole.jobId,
            }),
        ),
      );

      const failedResponse = responses.find((response) => {
        const code = Number(response?.code);

        return Number.isFinite(code) && ![200, 201].includes(code);
      });

      if (failedResponse) {
        throw new Error(
          failedResponse?.message || "Unable to notify partner.",
        );
      }

      showToast("Partner notified");
      setSelectedMatchIds([]);
      setSelectedMatchProfiles([]);
    } catch (error) {
      console.error("Failed to notify partner interest:", error);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to notify partner.",
      );
    } finally {
      setShortlistBulkActionLoading(false);
    }
  };

  const handleBulkShortlist = async () => {
    if (!activeRole?.jobId || !selectedMatchIds.length) return;

    const selectedProfiles = selectedMatchProfiles.filter(
      (profile) => !isMatchProfileSaved(profile),
    );

    if (!selectedProfiles.length) {
      showToast("The selected profiles are already in the shortlist.");
      return;
    }

    if (selectedProfiles.some((profile) => !getMatchProfileUserId(profile))) {
      showToast("Unable to shortlist a profile without a user ID.");
      return;
    }

    try {
      setShortlistBulkActionLoading(true);
      await ensureShortlistApiSuccess(
        await shortListPersonForJobRoleApi({
          jobId: activeRole.jobId,
          userIdArray: selectedProfiles.map((profile) =>
            getMatchProfileUserId(profile),
          ),
        }),
      );

      const nextProfiles = selectedProfiles.map((profile) => ({
        ...profile,
        saved: true,
      }));
      const nextState = addProfilesToRoleShortlist(activeRole, nextProfiles);

      setShortlists(nextState);
      setMatches((previous) =>
        previous.map(
          (profile) =>
            nextProfiles.find((item) => item.id === profile.id) || profile,
        ),
      );
      setSelectedShortlistRoleId(activeRole.jobId || activeRole.id);
      setSelectedMatchIds([]);
      setSelectedMatchProfiles([]);
      void getShortlistedJobRolesApiHandler(true);

      showToast("Added to shortlist");
    } catch (error) {
      console.error("Failed to add profiles to shortlist:", error);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to add profiles to shortlist.",
      );
    } finally {
      setShortlistBulkActionLoading(false);
    }
  };

  const openProfilePage = (
    profile,
    roleOverride = activeRole,
    returnView = hiringView,
  ) => {
    const detailPayload = {
      profile,
      role: roleOverride,
      returnView,
    };

    sessionStorage.setItem(
      "mmhHiringProfileDetail",
      JSON.stringify(detailPayload),
    );

    sessionStorage.setItem(
      HIRING_ACTIVE_ROLE_KEY,
      JSON.stringify(roleOverride),
    );

    sessionStorage.setItem(HIRING_ACTIVE_VIEW_KEY, returnView);

    navigate(
      `/talent-development/hiring/profiles/${encodeURIComponent(profile.id)}`,
      {
        state: detailPayload,
      },
    );
  };

  const activeRoleShortDescription = getRoleShortDescription(activeRole);

  const activeRoleFullDescription = getRoleFullDescription(activeRole);

  const activeRoleDescriptionPreview = getRoleDescriptionPreview(
    activeRoleFullDescription,
  );

  const renderShortlistCards = () => (
    <section className="mmh-shortlist-overview">
      <div className="mmh-shortlist-overview-heading">
        <div>
          {/*<span className="mmh-shortlist-eyebrow">
            Role-specific shortlists
          </span>*/}
          <h2>Shortlisted Profiles</h2>
          <p>
            Open a role shortlist to review its organization and partner-shared
            profiles together.
          </p>
        </div>
      </div>

      <div className="mmh-hiring-controls-shell mmh-shortlist-overview-controls">
        <div className="mmh-smart-filter-panel">
          <div className="mmh-smart-filter-top-bar">
            <div className="mmh-smart-filter-left">
              <div className="mmh-smart-filter-info">
                {getShortlistOverviewFilterCount() > 0 && (
                  <>
                    <span className="mmh-smart-filter-badge">
                      {getShortlistOverviewFilterCount()}
                    </span>

                    <span>filters applied</span>

                    <button
                      type="button"
                      className="mmh-smart-clear-all"
                      onClick={clearShortlistOverviewFilters}
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="mmh-smart-filter-right">
              {showShortlistOverviewSearch && (
                <form
                  className="mmh-smart-search-inline-row"
                  onSubmit={(event) => {
                    event.preventDefault();

                    setShortlistOverviewSearchTerm(
                      shortlistOverviewSearchInput.trim(),
                    );
                    setShortlistOverviewCurrentPage(1);
                    setShowShortlistOverviewSearch(false);
                  }}
                >
                  <input
                    type="text"
                    className="mmh-smart-search-inline-input"
                    placeholder="Search shortlist or role name"
                    value={shortlistOverviewSearchInput}
                    onChange={(event) =>
                      setShortlistOverviewSearchInput(event.target.value)
                    }
                    autoFocus
                  />

                  {(shortlistOverviewSearchInput ||
                    shortlistOverviewSearchTerm) && (
                    <button
                      type="button"
                      className="mmh-smart-search-clear"
                      title="Clear search"
                      onClick={() => {
                        setShortlistOverviewSearchInput("");
                        setShortlistOverviewSearchTerm("");
                        setShortlistOverviewCurrentPage(1);
                      }}
                    >
                      <X size={18} />
                    </button>
                  )}

                  <button
                    type="submit"
                    className="mmh-smart-filter-icon-btn active"
                    title="Search"
                  >
                    <Search size={18} />
                  </button>
                </form>
              )}

              <div className="mmh-smart-filter-icons-right">
                {!showShortlistOverviewSearch && (
                  <button
                    type="button"
                    className="mmh-smart-filter-icon-btn"
                    title="Search"
                    onClick={() => {
                      setShortlistOverviewSearchInput(
                        shortlistOverviewSearchTerm,
                      );
                      setShowShortlistOverviewSearch(true);
                      setShowShortlistOverviewFilters(false);
                    }}
                  >
                    <Search size={18} />
                  </button>
                )}

                <div className="mmh-smart-filter-separator" />

                <button
                  type="button"
                  className={`mmh-smart-filter-icon-btn ${
                    showShortlistOverviewFilters ? "active" : ""
                  }`}
                  title="Filters"
                  onClick={() => {
                    setShowShortlistOverviewFilters((previous) => !previous);
                    setShowShortlistOverviewSearch(false);
                  }}
                >
                  <Filter size={18} />
                </button>
              </div>
            </div>
          </div>

          {showShortlistOverviewFilters && (
            <>
              <div className="mmh-smart-filter-divider" />

              <div className="mmh-shortlist-overview-filter-content">
                <div className="mmh-shortlist-overview-filter-grid">
                  <label className="mmh-shortlist-overview-filter-field">
                    <span className="mmh-shortlist-overview-filter-label">
                      <Building2 size={15} />
                      Department
                    </span>

                    <span className="mmh-shortlist-overview-select-wrap">
                      <select
                        value={shortlistOverviewDepartment}
                        onChange={(event) => {
                          setShortlistOverviewDepartment(event.target.value);
                          setShortlistOverviewCurrentPage(1);
                        }}
                        className={
                          shortlistOverviewDepartment !== "All" ? "active" : ""
                        }
                      >
                        <option value="All">All departments</option>

                        {shortlistOverviewDepartments.map((department) => (
                          <option key={department} value={department}>
                            {department}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={16} />
                    </span>
                  </label>

                  <label className="mmh-shortlist-overview-filter-field">
                    <span className="mmh-shortlist-overview-filter-label">
                      <Briefcase size={15} />
                      Level
                    </span>

                    <span className="mmh-shortlist-overview-select-wrap">
                      <select
                        value={shortlistOverviewLevel}
                        onChange={(event) => {
                          setShortlistOverviewLevel(event.target.value);
                          setShortlistOverviewCurrentPage(1);
                        }}
                        className={
                          shortlistOverviewLevel !== "All" ? "active" : ""
                        }
                      >
                        <option value="All">All levels</option>

                        {shortlistOverviewLevels.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>

                      <ChevronDown size={16} />
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {shortlistOverviewLoading ? (
        <div className="mmh-role-empty-state">
          <Search size={22} />
          <strong>Loading shortlisted job roles...</strong>
          <span>Fetching the shortlist overview from the API.</span>
        </div>
      ) : shortlistOverviewError ? (
        <div className="mmh-role-empty-state">
          <AlertTriangle size={22} />
          <strong>{shortlistOverviewError}</strong>
          <span>Try loading the shortlist tab again.</span>
          <button
            type="button"
            className="mmh-btn mmh-btn-secondary"
            onClick={() => getShortlistedJobRolesApiHandler(true)}
          >
            Try Again
          </button>
        </div>
      ) : (
        <>
          <div className="mmh-shortlist-card-grid">
            {shortlistOverviewRoles.map((shortlist) => {
              const organizationCount =
                Number(shortlist.organizationProfilesCount) || 0;
              const partnerSharedCount =
                Number(shortlist.partnerSharedProfilesCount) || 0;
              const totalCount =
                Number(shortlist.shortlistedProfilesCount) ||
                organizationCount + partnerSharedCount;

              return (
                <article key={shortlist.id} className="mmh-shortlist-card">
                  <div className="mmh-shortlist-card-top">
                    <span className="mmh-role-card-icon">
                      <Briefcase size={18} />
                    </span>

                    <span className="mmh-shortlist-card-count">
                      {totalCount}
                    </span>
                  </div>

                  <div className="mmh-shortlist-card-copy">
                    <h3>{shortlist.name}</h3>

                    <p>{shortlist.role.title}</p>

                    <div className="mmh-shortlist-card-role-meta">
                      <span>{shortlist.role.department}</span>
                      <span>{shortlist.role.level}</span>
                    </div>
                  </div>

                  <div className="mmh-shortlist-card-badges">
                    <span className="mmh-badge mmh-badge-internal">
                      {organizationCount} Organization Profile
                      {organizationCount === 1 ? "" : "s"}
                    </span>

                    <span className="mmh-badge mmh-badge-external">
                      {partnerSharedCount} Partner Shared
                    </span>
                  </div>

                  <div className="mmh-shortlist-card-footer">
                    <span>
                      Updated{" "}
                      {new Date(shortlist.updatedAt).toLocaleDateString()}
                    </span>

                    <button
                      type="button"
                      className="mmh-btn mmh-btn-primary"
                      onClick={() => openShortlist(shortlist)}
                    >
                      Open Shortlist
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {!shortlistOverviewRoles.length && !shortlistOverviewLoading && (
            <div className="mmh-role-empty-state">
              <Search size={22} />
              <strong>No shortlists match the current filters.</strong>
              <span>Clear or adjust the search and filters.</span>
            </div>
          )}

          {shortlistOverviewTotal > 0 && (
            <Pagination
              loading={shortlistOverviewLoading}
              total={shortlistOverviewTotal}
              currentPage={shortlistOverviewCurrentPage}
              itemsPerPage={shortlistOverviewItemsPerPage}
              currentPageCount={shortlistOverviewRoles.length}
              onPageChange={(page) => {
                setShortlistOverviewCurrentPage(page);
              }}
              pageSizeOptions={SHORTLIST_OVERVIEW_PAGE_SIZE_OPTIONS}
              onPageSizeChange={(size) => {
                setShortlistOverviewItemsPerPage(size);
                setShortlistOverviewCurrentPage(1);
              }}
              infoRenderer={({ start, end, total }) =>
                `Showing ${start}-${end} of ${total} shortlisted roles`
              }
            />
          )}
        </>
      )}
    </section>
  );
  const renderShortlistView = () => (
    <section className="mmh-shortlist-shell">
      {!selectedShortlist ? (
        renderShortlistCards()
      ) : (
        <>
          <div className="mmh-shortlist-header">
            <div className="mmh-shortlist-header-copy">
              <button
                type="button"
                className="mmh-shortlist-back-button"
                onClick={closeShortlist}
              >
                <ChevronLeft size={16} />
                Back to Shortlists
              </button>

              {/*<span className="mmh-shortlist-eyebrow">
                Role-specific shortlist
              </span>*/}

              <div className="mmh-shortlist-title-section">
                <div className="mmh-shortlist-inline-title">
                  {renamingShortlist ? (
                    <>
                      <input
                        type="text"
                        className="mmh-shortlist-title-input"
                        value={shortlistNameInput}
                        onChange={(event) =>
                          setShortlistNameInput(event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleSaveShortlistName();
                          }

                          if (event.key === "Escape") {
                            setShortlistNameInput(
                              selectedShortlist?.name || "",
                            );
                            setRenamingShortlist(false);
                          }
                        }}
                        aria-label="Shortlist title"
                        autoFocus
                      />

                      <button
                        type="button"
                        className="mmh-shortlist-title-action save"
                        onClick={handleSaveShortlistName}
                        disabled={!shortlistNameInput.trim()}
                        title="Save title"
                        aria-label="Save shortlist title"
                      >
                        <Check size={16} />
                      </button>

                      <button
                        type="button"
                        className="mmh-shortlist-title-action"
                        onClick={() => {
                          setShortlistNameInput(selectedShortlist?.name || "");
                          setRenamingShortlist(false);
                        }}
                        title="Cancel editing"
                        aria-label="Cancel editing shortlist title"
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <h2>{selectedShortlist?.name}</h2>

                      <button
                        type="button"
                        className="mmh-shortlist-title-edit-btn"
                        onClick={() => {
                          setShortlistNameInput(selectedShortlist?.name || "");
                          setRenamingShortlist(true);
                        }}
                        title="Edit shortlist title"
                        aria-label="Edit shortlist title"
                      >
                        <Pencil size={15} />
                      </button>
                    </>
                  )}
                </div>

                <p className="mmh-shortlist-title-description">
                  Profiles shortlisted specifically for{" "}
                  <strong>{selectedShortlist?.role?.title}</strong>.
                </p>
              </div>
            </div>
          </div>

          <div className="mmh-hiring-controls-shell mmh-shortlist-controls-shell">
            <div className="mmh-smart-filter-panel">
              <div className="mmh-smart-filter-top-bar">
                <div className="mmh-smart-filter-left">
                  <div className="mmh-smart-filter-info">
                    {getAppliedShortlistFiltersCount() > 0 && (
                      <>
                        <span className="mmh-smart-filter-badge">
                          {getAppliedShortlistFiltersCount()}
                        </span>

                        <span>filters applied</span>

                        <button
                          type="button"
                          className="mmh-smart-clear-all"
                          onClick={clearShortlistFilters}
                        >
                          Clear
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="mmh-smart-filter-right">
                  {showShortlistSearch && (
                    <form
                      className="mmh-smart-search-inline-row"
                      onSubmit={(event) => {
                        event.preventDefault();

                        setShortlistSearchTerm(shortlistSearchInput.trim());

                        setShortlistCurrentPage(1);
                        setShowShortlistSearch(false);
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search profile, role, availability or partner"
                        value={shortlistSearchInput}
                        onChange={(event) =>
                          setShortlistSearchInput(event.target.value)
                        }
                        className="mmh-smart-search-inline-input"
                        autoFocus
                      />

                      {(shortlistSearchInput || shortlistSearchTerm) && (
                        <button
                          type="button"
                          className="mmh-smart-search-clear"
                          onClick={() => {
                            setShortlistSearchInput("");
                            setShortlistSearchTerm("");
                            setShortlistCurrentPage(1);
                          }}
                          title="Clear search"
                          aria-label="Clear shortlist search"
                        >
                          <X size={18} />
                        </button>
                      )}

                      <button
                        type="submit"
                        className="mmh-smart-filter-icon-btn active"
                        title="Search"
                        aria-label="Search shortlisted profiles"
                      >
                        <Search size={18} />
                      </button>
                    </form>
                  )}

                  <div className="mmh-smart-filter-icons-right">
                    {!showShortlistSearch && (
                      <button
                        type="button"
                        className="mmh-smart-filter-icon-btn"
                        onClick={() => {
                          setShortlistSearchInput(shortlistSearchTerm);
                          setShowShortlistSearch(true);
                          setShowShortlistFilters(false);
                        }}
                        title="Search"
                        aria-label="Open shortlist search"
                      >
                        <Search size={18} />
                      </button>
                    )}

                    <div className="mmh-smart-filter-separator" />

                    <button
                      type="button"
                      className={`mmh-smart-filter-icon-btn ${
                        showShortlistFilters ? "active" : ""
                      }`}
                      onClick={() => {
                        setShowShortlistFilters((previous) => !previous);
                        setShowShortlistSearch(false);
                      }}
                      title="Filters"
                      aria-label="Toggle shortlist filters"
                    >
                      <Filter size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {showShortlistFilters && (
                <>
                  <div className="mmh-smart-filter-divider" />

                  <div className="mmh-smart-filter-content">
                    <div className="mmh-smart-filter-grid">
                      <div className="mmh-smart-filter-item">
                        <label>Source</label>

                        <select
                          value={shortlistSourceFilter}
                          onChange={(event) => {
                            setShortlistSourceFilter(event.target.value);
                            setShortlistCurrentPage(1);
                          }}
                          className={`mmh-smart-filter-select ${
                            shortlistSourceFilter !== "All"
                              ? "smart-active"
                              : ""
                          }`}
                        >
                          <option value="All">All profiles</option>

                          {availableMatchSourceOptions.map((source) => (
                            <option key={source} value={source}>
                              {source === "Your Organization"
                                ? "Organization Profile"
                                : source}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mmh-smart-filter-item">
                        <label>Availability</label>

                        <select
                          value={shortlistAvailabilityFilter}
                          onChange={(event) => {
                            setShortlistAvailabilityFilter(event.target.value);

                            setShortlistCurrentPage(1);
                          }}
                          className={`mmh-smart-filter-select ${
                            shortlistAvailabilityFilter !== "All"
                              ? "smart-active"
                              : ""
                          }`}
                        >
                          <option value="All">All availability</option>

                          {availableMatchAvailabilityOptions.map(
                            (availability) => (
                              <option key={availability} value={availability}>
                                {availability}
                              </option>
                            ),
                          )}

                          <option value={MATCH_UNKNOWN_AVAILABILITY_FILTER}>
                            Unknown
                          </option>
                        </select>
                      </div>

                      <div className="mmh-smart-filter-item">
                        <label>Partner</label>

                        <select
                          value={shortlistPartnerFilter}
                          disabled={partnersLoading}
                          title={partnersError || "Filter by partner"}
                          onChange={(event) => {
                            setShortlistPartnerFilter(event.target.value);
                            setShortlistCurrentPage(1);
                          }}
                          className={`mmh-smart-filter-select ${
                            shortlistPartnerFilter !== "All"
                              ? "smart-active"
                              : ""
                          }`}
                        >
                          <option value="All">
                            {partnersLoading
                              ? "Loading partners..."
                              : "All partners"}
                          </option>

                          {shortlistPartnerOptions.map((partner) => (
                            <option key={partner.id} value={partner.id}>
                              {partner.label}
                              {partner.partnerOrgCode
                                ? ` (${partner.partnerOrgCode})`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="u-page-actions mmh-match-bulk-toolbar mmh-shortlist-bulk-toolbar">
            <label className="select-all-wrap">
              <input
                type="checkbox"
                checked={allShortlistProfilesSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someShortlistProfilesSelected;
                  }
                }}
                disabled={
                  shortlistLoading ||
                  shortlistSelectAllLoading ||
                  shortlistBulkActionLoading ||
                  shortlistTotal === 0
                }
                onChange={(event) =>
                  handleSelectAllShortlistProfiles(event.target.checked)
                }
                aria-label="Select all shortlisted profiles matching the current filters"
              />

              <span>
                {shortlistSelectAllLoading ? "Selecting all..." : "Select All"}
                {!shortlistSelectAllLoading && shortlistSelectedIds.length > 0
                  ? ` (${shortlistSelectedIds.length})`
                  : ""}
              </span>
            </label>

            <BulkAction
              options={[
                {
                  value: "remove-shortlisted",
                  label: "Remove from Shortlist",
                },
              ]}
              selectedIds={shortlistSelectedIds}
              onAction={(action) => {
                if (action === "remove-shortlisted") {
                  handleRemoveShortlistedProfiles(shortlistSelectedIds);
                }
              }}
              label="Bulk Action"
              placeholder="Select bulk action"
              requireSelection={true}
              disabled={shortlistBulkActionLoading}
            />
          </div>

          <div className="mmh-table-container mmh-shortlist-table-wrap">
            <table className="mmh-table mmh-shortlist-table">
              <thead>
                <tr>
                  <th className="mmh-shortlist-select-column">
                    <input
                      type="checkbox"
                      checked={allShortlistProfilesSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = someShortlistProfilesSelected;
                        }
                      }}
                      disabled={
                        shortlistLoading ||
                        shortlistSelectAllLoading ||
                        shortlistBulkActionLoading ||
                        shortlistTotal === 0
                      }
                      onChange={(event) =>
                        handleSelectAllShortlistProfiles(event.target.checked)
                      }
                      aria-label="Select all shortlisted profiles matching the current filters"
                    />
                  </th>

                  <th>Profile</th>
                  <th>Source</th>
                  <th>Role</th>
                  <th>Match</th>
                  <th>Availability</th>
                  <th>Partner</th>
                  <th aria-label="Remove"></th>
                </tr>
              </thead>

              <tbody>
                {shortlistLoading && (
                  <tr>
                    <td colSpan={8} className="mmh-match-list-empty-cell">
                      Loading shortlisted profiles...
                    </td>
                  </tr>
                )}

                {!shortlistLoading && shortlistError && (
                  <tr>
                    <td colSpan={8} className="mmh-match-list-empty-cell">
                      <div>
                        <span>{shortlistError}</span>
                        <button
                          type="button"
                          className="mmh-btn mmh-btn-secondary"
                          onClick={openShortlistedJobRoleApiHandler}
                        >
                          Try Again
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {!shortlistLoading &&
                  !shortlistError &&
                  currentShortlistPageItems.map((profile) => (
                    <tr
                      key={profile.id}
                      onClick={() =>
                        openProfilePage(
                          profile,
                          selectedShortlist.role,
                          "shortlist",
                        )
                      }
                    >
                      <td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={shortlistSelectedIds.includes(profile.id)}
                          onChange={(event) =>
                            handleSelectShortlistProfile(
                              profile.id,
                              event.target.checked,
                            )
                          }
                          aria-label={`Select ${profile.name || ""}`}
                        />
                      </td>

                      <td>{profile.name || ""}</td>

                      <td>
                        <span
                          className={`mmh-badge ${
                            profile.isInternal
                              ? "mmh-badge-internal"
                              : "mmh-badge-external"
                          }`}
                        >
                          {getProfileSourceLabel(profile)}
                        </span>
                      </td>

                      <td>{selectedShortlist.role.title}</td>

                      <td className="mmh-match-cell">
                        <span
                          className={`mmh-match-score-badge ${getMatchScoreClass(
                            profile.match,
                          )}`}
                        >
                          {profile.match}%
                        </span>
                      </td>

                      <td>{profile.availability || "Unknown"}</td>
                      <td>{profile.partner || "-"}</td>

                      <td onClick={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          className="mmh-icon-btn mmh-shortlist-remove-btn"
                          onClick={() =>
                            handleRemoveShortlistedProfiles([profile.id])
                          }
                          disabled={shortlistBulkActionLoading}
                          title="Remove from shortlist"
                          aria-label={`Remove ${profile.name || ""} from shortlist`}
                        >
                          <Trash2 size={17} />
                        </button>
                      </td>
                    </tr>
                  ))}

                {!shortlistLoading &&
                  !shortlistError &&
                  !currentShortlistPageItems.length && (
                    <tr>
                      <td colSpan={8} className="mmh-match-list-empty-cell">
                        No shortlisted profiles match the current filters.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
          {shortlistTotal > 0 && !shortlistError && (
            <Pagination
              loading={shortlistLoading}
              total={shortlistTotal}
              currentPage={shortlistCurrentPage}
              itemsPerPage={shortlistItemsPerPage}
              currentPageCount={currentShortlistPageItems.length}
              onPageChange={(page) => {
                setShortlistCurrentPage(page);
              }}
              pageSizeOptions={SHORTLIST_PAGE_SIZE_OPTIONS}
              onPageSizeChange={(size) => {
                setShortlistItemsPerPage(size);
                setShortlistCurrentPage(1);
              }}
              infoRenderer={({ start, end, total }) =>
                `Showing ${start}-${end} of ${total} shortlisted profiles`
              }
            />
          )}
        </>
      )}
    </section>
  );

  if (!partnerCollaboration.enabled) {
    return (
      <div className="mmh-page">
        <main className="mmh-page-inner">
          <div className="mmh-hiring-back-row">
            <BackButton />
          </div>
          <header className="mmh-page-header">
            <div className="mmh-page-header-icon">
              <Briefcase size={32} />
            </div>
            <h1>Hiring</h1>
            <p>
              Find matching profiles for roles across your organization and
              partner network.
            </p>
          </header>
          <section className="mmh-hiring-disabled-card">
            <div className="mmh-hiring-disabled-icon">
              <Briefcase size={24} />
            </div>
            <h2>Partner Collaboration is not enabled</h2>
            <p>
              Enable Partner Collaboration from Organization Information -&gt;
              Partners before using Hiring and partner-shared profiles.
            </p>
            <button
              type="button"
              className="mmh-btn mmh-btn-primary"
              onClick={() => navigate("/settings")}
            >
              Open Settings
            </button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="mmh-page">
      <main className="mmh-page-inner">
        <div
          className="mmh-hiring-back-row"
          onClickCapture={(event) => {
            if (hiringView === "matches" && activeRole) {
              event.preventDefault();
              event.stopPropagation();
              handleChangeRole();
            }
          }}
        >
          <BackButton />
        </div>
        <header className="mmh-page-header">
          <div className="mmh-page-header-icon">
            <Briefcase size={32} />
          </div>
          <h1>Hiring</h1>
          <p>
            Find matching profiles for roles across your organization and
            partner network.
          </p>
        </header>

        <div
          className="mmh-role-tabs mmh-hiring-feature-tabs"
          role="tablist"
          aria-label="Hiring sections"
        >
          <button
            type="button"
            role="tab"
            aria-selected={hiringView === "matches"}
            className={hiringView === "matches" ? "active" : ""}
            onClick={() => handleHiringViewChange("matches")}
          >
            <span className="mmh-role-tab-content">
              <Search size={17} />

              <span className="mmh-role-tab-label">Roles and Profiles</span>
            </span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={hiringView === "shortlist"}
            className={hiringView === "shortlist" ? "active" : ""}
            onClick={() => {
              closeShortlist();
              handleHiringViewChange("shortlist");
            }}
          >
            <span className="mmh-role-tab-content">
              <Bookmark size={17} />

              <span className="mmh-role-tab-label">Shortlist</span>

              <span className="mmh-role-tab-count">
                {shortlistOverviewBadgeTotal}
              </span>
            </span>
          </button>
        </div>

        {hiringView === "shortlist" ? (
          renderShortlistView()
        ) : !activeRole ? (
          <section className="mmh-role-directory">
            <div className="mmh-role-directory-head">
              <div>
                <h2>Select Job Role</h2>
                <p>
                  Choose a role, then find the strongest available profile
                  matches.
                </p>
              </div>
              {selectedRole && (
                <span className="mmh-selected-role-pill">
                  Selected: {selectedRole.title}
                </span>
              )}
            </div>

            <div
              className="mmh-role-tabs mmh-role-source-tabs"
              role="tablist"
              aria-label="Hiring roles"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeRoleTab === "organization"}
                className={activeRoleTab === "organization" ? "active" : ""}
                onClick={() => handleRoleTabChange("organization")}
              >
                <span className="mmh-role-tab-content">
                  <Building2 size={17} />
                  <span className="mmh-role-tab-label">
                    My Organization Roles
                  </span>
                  <span className="mmh-role-tab-count">
                    {organizationRolesTotal}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeRoleTab === "partner"}
                className={activeRoleTab === "partner" ? "active" : ""}
                onClick={() => handleRoleTabChange("partner")}
              >
                <span className="mmh-role-tab-content">
                  <Users size={17} />
                  <span className="mmh-role-tab-label">Partner Roles</span>
                  <span className="mmh-role-tab-count">
                    {partnerRolesTotal}
                  </span>
                </span>
              </button>
            </div>

            <div className="mmh-role-controls-shell">
              <div className="mmh-smart-filter-panel">
                <div className="mmh-smart-filter-top-bar">
                  <div className="mmh-smart-filter-left">
                    <div className="mmh-smart-filter-info">
                      {getAppliedRoleFiltersCount() > 0 && (
                        <>
                          <span className="mmh-smart-filter-badge">
                            {getAppliedRoleFiltersCount()}
                          </span>
                          <span>filters applied</span>
                          <button
                            type="button"
                            className="mmh-smart-clear-all"
                            onClick={clearRoleFilters}
                          >
                            Clear
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mmh-smart-filter-right">
                    {showRoleSearchBar && (
                      <form
                        className="mmh-smart-search-inline-row"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleRoleSearchButtonClick();
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search roles by title, department, level or skill"
                          value={roleSearchInputValue}
                          onChange={(event) =>
                            setRoleSearchInputValue(event.target.value)
                          }
                          className="mmh-smart-search-inline-input"
                          autoFocus
                        />

                        {(roleSearchInputValue || roleSearchTerm) && (
                          <button
                            type="button"
                            className="mmh-smart-search-clear"
                            onClick={handleClearRoleSearch}
                            title="Clear search"
                          >
                            <X size={18} />
                          </button>
                        )}

                        <button
                          type="submit"
                          className="mmh-smart-filter-icon-btn active"
                          title="Search"
                        >
                          <Search size={18} />
                        </button>
                      </form>
                    )}

                    <div className="mmh-smart-filter-icons-right">
                      {!showRoleSearchBar && (
                        <button
                          type="button"
                          className="mmh-smart-filter-icon-btn"
                          onClick={() => {
                            setRoleSearchInputValue(roleSearchTerm);
                            setShowRoleSearchBar(true);
                            setShowRoleFiltersPanel(false);
                          }}
                          title="Search"
                        >
                          <Search size={18} />
                        </button>
                      )}

                      <div className="mmh-smart-filter-separator" />

                      <button
                        type="button"
                        className={`mmh-smart-filter-icon-btn ${
                          showRoleFiltersPanel ? "active" : ""
                        }`}
                        onClick={() => {
                          setShowRoleFiltersPanel((previous) => !previous);
                          setShowRoleSearchBar(false);
                        }}
                        title="Filters"
                      >
                        <Filter size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {showRoleFiltersPanel && (
                  <>
                    <div className="mmh-smart-filter-divider" />
                    <div className="mmh-smart-filter-content">
                      <div className="mmh-smart-filter-grid">
                        <div className="mmh-smart-filter-item">
                          <label>Department</label>
                          <select
                            value={roleDepartmentFilter}
                            onChange={(event) => {
                              setRoleDepartmentFilter(event.target.value);
                              setRolePageByTab((previous) => ({
                                ...previous,
                                [activeRoleTab]: 1,
                              }));
                            }}
                            className={`mmh-smart-filter-select ${
                              roleDepartmentFilter !== "All"
                                ? "smart-active"
                                : ""
                            }`}
                          >
                            <option value="All">All departments</option>
                            {roleDepartmentOptions.map((department) => (
                              <option key={department} value={department}>
                                {department}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mmh-smart-filter-item">
                          <label>Level</label>
                          <select
                            value={roleLevelFilter}
                            onChange={(event) => {
                              setRoleLevelFilter(event.target.value);
                              setRolePageByTab((previous) => ({
                                ...previous,
                                [activeRoleTab]: 1,
                              }));
                            }}
                            className={`mmh-smart-filter-select ${
                              roleLevelFilter !== "All" ? "smart-active" : ""
                            }`}
                          >
                            <option value="All">All levels</option>
                            {roleLevelOptions.map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                        </div>

                        {activeRoleTab === "partner" && (
                          <div className="mmh-smart-filter-item">
                            <label>Partner</label>
                            <div
                              className="mmh-partner-lookup"
                              ref={partnerLookupRef}
                            >
                              <div className="mmh-partner-lookup-control">
                                <Search size={16} />
                                <input
                                  type="text"
                                  value={rolePartnerLookup}
                                  onFocus={() => setShowPartnerLookup(true)}
                                  onChange={(event) => {
                                    setRolePartnerLookup(event.target.value);
                                    setShowPartnerLookup(true);
                                    if (!event.target.value) {
                                      setRolePartnerFilter("All");
                                    }
                                  }}
                                  placeholder="Search and select partner"
                                />
                                {(rolePartnerLookup ||
                                  rolePartnerFilter !== "All") && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setRolePartnerLookup("");
                                      setRolePartnerFilter("All");
                                      setRolePageByTab((previous) => ({
                                        ...previous,
                                        partner: 1,
                                      }));
                                    }}
                                    aria-label="Clear partner"
                                  >
                                    <X size={15} />
                                  </button>
                                )}
                              </div>

                              {showPartnerLookup && (
                                <div className="mmh-partner-lookup-menu">
                                  <button
                                    type="button"
                                    className={
                                      rolePartnerFilter === "All"
                                        ? "selected"
                                        : ""
                                    }
                                    onClick={() => {
                                      setRolePartnerFilter("All");
                                      setRolePartnerLookup("");
                                      setShowPartnerLookup(false);
                                      setRolePageByTab((previous) => ({
                                        ...previous,
                                        partner: 1,
                                      }));
                                    }}
                                  >
                                    All partners
                                  </button>

                                  {visiblePartnerLookupOptions.map(
                                    (partnerOption) => (
                                      <button
                                        type="button"
                                        key={partnerOption.id}
                                        className={
                                          rolePartnerFilter === partnerOption.id
                                            ? "selected"
                                            : ""
                                        }
                                        onClick={() => {
                                          setRolePartnerFilter(
                                            partnerOption.id,
                                          );
                                          setRolePartnerLookup(
                                            partnerOption.label,
                                          );
                                          setShowPartnerLookup(false);
                                          setRolePageByTab((previous) => ({
                                            ...previous,
                                            partner: 1,
                                          }));
                                        }}
                                      >
                                        {partnerOption.label}
                                      </button>
                                    ),
                                  )}

                                  {!visiblePartnerLookupOptions.length && (
                                    <div className="mmh-partner-lookup-empty">
                                      No partner found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {activeRoleTab === "organization" && (
                          <label className="mmh-smart-checkbox-item">
                            <input
                              type="checkbox"
                              checked={showOnlyOpenRoles}
                              onChange={(event) => {
                                setShowOnlyOpenRoles(event.target.checked);

                                setRolePageByTab((previous) => ({
                                  ...previous,
                                  organization: 1,
                                }));
                              }}
                            />

                            <span>Show only open roles</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {activeRolesLoading && (
              <div className="mmh-role-empty-state">
                <strong>Loading job roles...</strong>
                <span>Please wait while the roles are being loaded.</span>
              </div>
            )}

            {!activeRolesLoading && activeRolesError && (
              <div className="mmh-role-empty-state">
                <AlertTriangle size={22} />

                <strong>{activeRolesError}</strong>

                <button
                  type="button"
                  className="mmh-btn mmh-btn-secondary"
                  onClick={() => {
                    if (activeRoleTab === "organization") {
                      handleRolesDataListApi(
                        rolePageByTab.organization,
                        roleItemsPerPage,
                      );
                    } else {
                      handlePartnerSharedRolesApi(
                        rolePageByTab.partner,
                        roleItemsPerPage,
                      );
                    }
                  }}
                >
                  Try Again
                </button>
              </div>
            )}

            {!activeRolesLoading && !activeRolesError && (
              <div className="mmh-role-grid">
                {paginatedRoles.map((role) => {
                  const isSelected = selectedRoleId === role.id;
                  return (
                    <div
                      key={role.id}
                      role="button"
                      tabIndex={0}
                      className={`mmh-short-role-card ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() => setSelectedRoleId(role.id)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;

                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedRoleId(role.id);
                        }
                      }}
                    >
                      <div className="mmh-short-role-card-top">
                        <span className="mmh-role-card-icon">
                          {activeRoleTab === "partner" ? (
                            <Users size={18} />
                          ) : (
                            <Building2 size={18} />
                          )}
                        </span>

                        <span
                          className={`mmh-role-status ${getRoleCardStatus(
                            role,
                          ).toLowerCase()}`}
                        >
                          {getRoleCardStatus(role)}
                        </span>
                      </div>

                      <h3>{role.title}</h3>

                      <p>{getRoleShortDescription(role)}</p>

                      <button
                        type="button"
                        className="mmh-role-card-description-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDescriptionRole(role);
                        }}
                      >
                        View full description
                      </button>

                      <div className="mmh-short-role-meta">
                        <div className="mmh-short-role-meta-item">
                          <span className="mmh-short-role-meta-label">
                            Department
                          </span>

                          <strong className="mmh-short-role-meta-value">
                            {role.department || "Not specified"}
                          </strong>
                        </div>

                        <div className="mmh-short-role-meta-item">
                          <span className="mmh-short-role-meta-label">
                            Level
                          </span>

                          <strong className="mmh-short-role-meta-value">
                            {role.level || "Not specified"}
                          </strong>
                        </div>
                      </div>

                      {role.partner && (
                        <div className="mmh-short-role-partner">
                          <Users size={13} />
                          <span>{role.partner}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!activeRolesLoading &&
              !activeRolesError &&
              !rolesForActiveTab.length && (
                <div className="mmh-role-empty-state">
                  <AlertTriangle size={22} />
                  <strong>No roles match the current filters.</strong>
                  <span>Clear or adjust the filters to see more roles.</span>
                </div>
              )}

            {rolePaginationTotal > 0 && !activeRolesError && (
              <Pagination
                loading={activeRolesLoading}
                total={rolePaginationTotal}
                currentPage={activeRolePage}
                itemsPerPage={roleItemsPerPage}
                currentPageCount={paginatedRoles.length}
                onPageChange={(page) => {
                  setSelectedRoleId("");
                  setRolePageByTab((previous) => ({
                    ...previous,
                    [activeRoleTab]: page,
                  }));
                }}
                pageSizeOptions={ROLE_PAGE_SIZE_OPTIONS}
                onPageSizeChange={(size) => {
                  setRoleItemsPerPage(size);
                  setRolePageByTab((previous) => ({
                    ...previous,
                    [activeRoleTab]: 1,
                  }));
                }}
                infoRenderer={({ start, end, total }) =>
                  `Showing ${start}-${end} of ${total} ${
                    activeRoleTab === "partner" ? "partner" : "organization"
                  } roles`
                }
              />
            )}
            <div className="mmh-role-directory-actions">
              <button
                type="button"
                className="mmh-btn mmh-btn-secondary"
                onClick={clearRoleFilters}
              >
                Reset
              </button>
              <button
                type="button"
                className="mmh-btn mmh-btn-primary"
                onClick={handleFindMatches}
                disabled={!selectedRoleId}
              >
                <Search size={16} />
                Find Matches
              </button>
            </div>
          </section>
        ) : (
          <>
            <div className="mmh-role-search-card compact">
              <div className="mmh-compact-role-info">
                <span className="mmh-active-role-label">
                  Comparing matches against
                </span>
                <h2 className="mmh-compact-role-title">{activeRole.title}</h2>
                <div className="mmh-active-role-copy">
                  <p className="mmh-active-role-short-description">
                    {activeRoleShortDescription ||
                      "No short description has been added for this role."}
                  </p>

                  <div className="mmh-active-role-description">
                    <p className="mmh-compact-role-description">
                      {activeRoleDescriptionPreview ||
                        "No long description has been added for this role."}
                    </p>

                    {activeRoleFullDescription && (
                      <button
                        type="button"
                        className="mmh-role-description-toggle"
                        onClick={() => setDescriptionRole(activeRole)}
                      >
                        Show more
                      </button>
                    )}
                  </div>
                </div>

                <div className="mmh-compact-role-meta-clean">
                  <span>
                    <strong>Source:</strong>{" "}
                    {activeRole.source === "Partner"
                      ? "Partner Shared"
                      : "Your Organization"}
                  </span>
                  {activeRole.partner && (
                    <span>
                      <strong>Partner:</strong> {activeRole.partner}
                    </span>
                  )}
                  <span>
                    <strong>Department:</strong> {activeRole.department}
                  </span>
                  <span>
                    <strong>Level:</strong> {activeRole.level}
                  </span>
                </div>

                {/* Keep this removed intentionally. Do not bring skill pills back. */}
              </div>

              <div className="mmh-compact-actions">
                {/*<button
                  type="button"
                  className="mmh-btn mmh-btn-secondary"
                  onClick={handleChangeRole}
                >
                  Change Role
                </button>*/}
                <button
                  type="button"
                  className="mmh-btn mmh-btn-secondary"
                  onClick={() => {
                    handleChangeRole();
                    clearRoleFilters();
                  }}
                >
                  Clear Search
                </button>
              </div>
            </div>

            <div className="mmh-hiring-controls-shell">
              <div className="mmh-smart-filter-panel">
                <div className="mmh-smart-filter-top-bar">
                  <div className="mmh-smart-filter-left">
                    <div className="mmh-smart-filter-info">
                      {getAppliedMatchFiltersCount() > 0 && (
                        <>
                          <span className="mmh-smart-filter-badge">
                            {getAppliedMatchFiltersCount()}
                          </span>
                          <span>filters applied</span>
                          <button
                            type="button"
                            className="mmh-smart-clear-all"
                            onClick={clearMatchFilters}
                          >
                            Clear
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mmh-smart-filter-right">
                    {showMatchSearchBar && (
                      <form
                        className="mmh-smart-search-inline-row"
                        onSubmit={(event) => {
                          event.preventDefault();
                          setMatchSearchTerm(matchSearchInputValue.trim());
                          setMatchCurrentPage(1);
                          setShowMatchSearchBar(false);
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search by profile name or profile ID"
                          value={matchSearchInputValue}
                          onChange={(event) =>
                            setMatchSearchInputValue(event.target.value)
                          }
                          className="mmh-smart-search-inline-input"
                          autoFocus
                        />

                        {(matchSearchInputValue || matchSearchTerm) && (
                          <button
                            type="button"
                            className="mmh-smart-search-clear"
                            onClick={() => {
                              setMatchSearchInputValue("");
                              setMatchSearchTerm("");
                              setMatchCurrentPage(1);
                            }}
                            title="Clear search"
                          >
                            <X size={18} />
                          </button>
                        )}

                        <button
                          type="submit"
                          className="mmh-smart-filter-icon-btn active"
                          title="Search"
                        >
                          <Search size={18} />
                        </button>
                      </form>
                    )}

                    <div className="mmh-smart-filter-icons-right">
                      {!showMatchSearchBar && (
                        <button
                          type="button"
                          className="mmh-smart-filter-icon-btn"
                          onClick={() => {
                            setMatchSearchInputValue(matchSearchTerm);
                            setShowMatchSearchBar(true);
                            setShowMatchFiltersPanel(false);
                          }}
                          title="Search"
                        >
                          <Search size={18} />
                        </button>
                      )}

                      <div className="mmh-smart-filter-separator" />

                      <button
                        type="button"
                        className={`mmh-smart-filter-icon-btn ${
                          showMatchFiltersPanel ? "active" : ""
                        }`}
                        onClick={() => {
                          setShowMatchFiltersPanel((previous) => !previous);
                          setShowMatchSearchBar(false);
                        }}
                        title="Filters"
                      >
                        <Filter size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {showMatchFiltersPanel && (
                  <>
                    <div className="mmh-smart-filter-divider" />
                    <div className="mmh-smart-filter-content">
                      <div className="mmh-smart-filter-grid">
                        <div className="mmh-smart-filter-item">
                          <label>Source</label>
                          <select
                            value={filterSource}
                            onChange={(event) => {
                              const nextSource = event.target.value;
                              setFilterSource(nextSource);
                              if (
                                nextSource === "All" ||
                                isOrganizationMatchSource(nextSource)
                              ) {
                                setFilterPartner("All");
                              }
                              setMatchCurrentPage(1);
                            }}
                            className={`mmh-smart-filter-select ${
                              filterSource !== "All" ? "smart-active" : ""
                            }`}
                          >
                            <option value="All">All</option>
                            {availableMatchSourceOptions.map((source) => (
                              <option key={source} value={source}>
                                {source}
                              </option>
                            ))}
                          </select>
                        </div>

                        {filterSource !== "All" &&
                          !isOrganizationMatchSource(filterSource) && (
                            <div className="mmh-smart-filter-item">
                              <label>Partner</label>

                              <div
                                className="mmh-partner-lookup"
                                ref={matchPartnerLookupRef}
                              >
                                <div className="mmh-partner-lookup-control">
                                  <Search size={16} />

                                  <input
                                    type="text"
                                    value={matchPartnerLookup}
                                    placeholder="Search and select partner"
                                    onFocus={() =>
                                      setShowMatchPartnerLookup(true)
                                    }
                                    onChange={(event) => {
                                      const nextValue = event.target.value;

                                      setMatchPartnerLookup(nextValue);
                                      setShowMatchPartnerLookup(true);

                                      if (!nextValue) {
                                        setFilterPartner("All");
                                        setMatchCurrentPage(1);
                                      }
                                    }}
                                  />

                                  {(matchPartnerLookup ||
                                    filterPartner !== "All") && (
                                    <button
                                      type="button"
                                      aria-label="Clear selected partner"
                                      onClick={() => {
                                        setMatchPartnerLookup("");
                                        setFilterPartner("All");
                                        setShowMatchPartnerLookup(false);
                                        setMatchCurrentPage(1);
                                      }}
                                    >
                                      <X size={15} />
                                    </button>
                                  )}
                                </div>

                                {showMatchPartnerLookup && (
                                  <div className="mmh-partner-lookup-menu">
                                    <button
                                      type="button"
                                      className={
                                        filterPartner === "All"
                                          ? "selected"
                                          : ""
                                      }
                                      onClick={() => {
                                        setFilterPartner("All");
                                        setMatchPartnerLookup("");
                                        setShowMatchPartnerLookup(false);
                                        setMatchCurrentPage(1);
                                      }}
                                    >
                                      All partners
                                    </button>

                                    {visibleMatchPartnerOptions.map(
                                      (partner) => (
                                        <button
                                          type="button"
                                          key={partner}
                                          className={
                                            filterPartner === partner
                                              ? "selected"
                                              : ""
                                          }
                                          onClick={() => {
                                            setFilterPartner(partner);
                                            setMatchPartnerLookup(partner);
                                            setShowMatchPartnerLookup(false);
                                            setMatchCurrentPage(1);
                                          }}
                                        >
                                          {partner}
                                        </button>
                                      ),
                                    )}

                                    {!visibleMatchPartnerOptions.length && (
                                      <div className="mmh-partner-lookup-empty">
                                        No partner found
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        <div className="mmh-smart-filter-item">
                          <label>Availability</label>
                          <select
                            value={filterAvailability}
                            onChange={(event) => {
                              setFilterAvailability(event.target.value);
                              setMatchCurrentPage(1);
                            }}
                            className={`mmh-smart-filter-select ${
                              filterAvailability !== "All" ? "smart-active" : ""
                            }`}
                          >
                            <option value="All">All</option>

                            {availableMatchAvailabilityOptions.map(
                              (availability) => (
                                <option key={availability} value={availability}>
                                  {availability}
                                </option>
                              ),
                            )}

                            <option value={MATCH_UNKNOWN_AVAILABILITY_FILTER}>
                              Unknown
                            </option>
                          </select>
                        </div>

                        <div className="mmh-smart-filter-item">
                          <label>Match</label>
                          <select
                            value={filterMatch}
                            onChange={(event) => {
                              setFilterMatch(event.target.value);
                              setMatchCurrentPage(1);
                            }}
                            className={`mmh-smart-filter-select ${
                              filterMatch !== "All" ? "smart-active" : ""
                            }`}
                          >
                            <option value="All">All</option>
                            <option value="90%+">90%+</option>
                            <option value="80%+">80%+</option>
                            <option value="70%+">70%+</option>
                          </select>
                        </div>

                        <label className="mmh-smart-checkbox-item">
                          <input
                            type="checkbox"
                            checked={showSavedOnly}
                            onChange={(event) => {
                              setShowSavedOnly(event.target.checked);
                              setMatchCurrentPage(1);
                            }}
                          />
                          <span>Shortlisted only</span>
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="u-page-actions mmh-match-bulk-toolbar">
              <label className="select-all-wrap">
                <input
                  type="checkbox"
                  checked={allMatchProfilesSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someMatchProfilesSelected;
                    }
                  }}
                  disabled={
                    matchesLoading ||
                    matchSelectAllLoading ||
                    shortlistBulkActionLoading ||
                    matchesTotal === 0
                  }
                  onChange={(event) =>
                    handleSelectAllMatchProfiles(event.target.checked)
                  }
                  aria-label="Select all matching profiles across every page"
                />

                <span>
                  {matchSelectAllLoading ? "Selecting all..." : "Select All"}
                  {!matchSelectAllLoading && selectedMatchIds.length > 0
                    ? ` (${selectedMatchIds.length})`
                    : ""}
                </span>
              </label>

              <BulkAction
                options={[
                  {
                    value: "shortlist",
                    label: "Add to Shortlist",
                  },
                  {
                    value: "notify-interest",
                    label: "Notify Interest",
                  },
                ]}
                selectedIds={selectedMatchIds}
                onAction={(action) => {
                  if (action === "shortlist") {
                    handleBulkShortlist();
                  }

                  if (action === "notify-interest") {
                    handleBulkNotifyInterest();
                  }
                }}
                label="Bulk Action"
                placeholder="Select bulk action"
                requireSelection={true}
                disabled={shortlistBulkActionLoading}
              />
            </div>

            <section className="mmh-results-section">
              <div className="mmh-results-view-toolbar mmh-results-view-toolbar-static">
                <div className="mmh-results-view-title">
                  <span>Match Results</span>
                  <small>
                    {matchesLoading
                      ? "Loading profiles..."
                      : `${matchesTotal} profiles found`}
                  </small>
                </div>
              </div>

              <div className="mmh-table-container">
                <table className="mmh-table mmh-match-list-table">
                  <thead>
                    <tr>
                      <th className="mmh-match-select-column">
                        <input
                          type="checkbox"
                          checked={allMatchProfilesSelected}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate = someMatchProfilesSelected;
                            }
                          }}
                          disabled={
                            matchesLoading ||
                            matchSelectAllLoading ||
                            matchesTotal === 0
                          }
                          onChange={(event) =>
                            handleSelectAllMatchProfiles(event.target.checked)
                          }
                          aria-label="Select all matching profiles across every page"
                        />
                      </th>
                      <th>
                        <button
                          type="button"
                          className="mmh-sort-header"
                          onClick={() => handleSort("profile")}
                        >
                          Profile {renderSortChevrons("profile")}
                        </button>
                      </th>
                      <th>Source</th>
                      <th>
                        <button
                          type="button"
                          className="mmh-sort-header"
                          onClick={() => handleSort("match")}
                        >
                          Match {renderSortChevrons("match")}
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="mmh-sort-header"
                          onClick={() => handleSort("availability")}
                        >
                          Availability {renderSortChevrons("availability")}
                        </button>
                      </th>
                      <th>Partner</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {matchesLoading && (
                      <tr>
                        <td colSpan="7" className="mmh-match-list-empty-cell">
                          Loading matching profiles...
                        </td>
                      </tr>
                    )}

                    {!matchesLoading && matchesError && (
                      <tr>
                        <td colSpan="7" className="mmh-match-list-empty-cell">
                          <div>
                            <span>{matchesError}</span>
                            <button
                              type="button"
                              className="mmh-btn mmh-btn-secondary"
                              onClick={() =>
                                handleFindMatchesApi(activeRole || selectedRole)
                              }
                            >
                              Try Again
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {!matchesLoading &&
                      !matchesError &&
                      currentMatchPageItems.map((profile) => (
                        <tr
                          key={profile.id}
                          onClick={(event) => {
                            const interactiveTarget = event.target.closest?.(
                              "button, input, select, textarea, a, label, [role='button']",
                            );

                            if (interactiveTarget) return;

                            openProfilePage(profile);
                          }}
                        >
                          <td onClick={(event) => event.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedMatchIdSet.has(profile.id)}
                              onChange={(event) =>
                                handleSelectMatch(
                                  profile.id,
                                  event.target.checked,
                                )
                              }
                              aria-label={`Select ${profile.name || ""}`}
                            />
                          </td>
                          <td>
                            <div className="mmh-profile-cell">
                              <span className="mmh-profile-name">
                                {profile.name || ""}
                              </span>
                              {!profile.isInternal &&
                                isProtectedExternalProfileName(
                                  profile.name,
                                ) && (
                                  <span className="mmh-profile-sub">
                                    Identity Protected
                                  </span>
                                )}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`mmh-badge ${
                                profile.isInternal
                                  ? "mmh-badge-internal"
                                  : "mmh-badge-external"
                              }`}
                            >
                              {getMatchSourceLabel(profile)}
                            </span>
                          </td>
                          <td className="mmh-match-cell">
                            <span
                              className={`mmh-match-score-badge ${getMatchScoreClass(
                                profile.match,
                              )}`}
                            >
                              {profile.match}%
                            </span>
                          </td>
                          <td>{profile.availability || "Unknown"}</td>
                          <td>{profile.partner || "-"}</td>
                          <td
                            onClick={(event) => event.stopPropagation()}
                            onPointerDown={(event) => event.stopPropagation()}
                          >
                            <div className="mmh-actions-cell">
                              <button
                                type="button"
                                className={`mmh-icon-btn ${
                                  isMatchProfileSaved(profile) ? "saved" : ""
                                }`}
                                disabled={
                                  shortlistActionProfileId === profile.id ||
                                  shortlistBulkActionLoading
                                }
                                onClick={(event) =>
                                  handleToggleShortlist(event, profile)
                                }
                                title={
                                  isMatchProfileSaved(profile)
                                    ? "Remove from shortlist"
                                    : "Add to shortlist"
                                }
                                aria-label={
                                  isMatchProfileSaved(profile)
                                    ? "Remove profile from shortlist"
                                    : "Add profile to shortlist"
                                }
                              >
                                <Bookmark
                                  size={18}
                                  fill={
                                    isMatchProfileSaved(profile)
                                      ? "currentColor"
                                      : "none"
                                  }
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}

                    {!matchesLoading &&
                      !matchesError &&
                      !currentMatchPageItems.length && (
                        <tr>
                          <td colSpan="7" className="mmh-match-list-empty-cell">
                            No matches found. Try adjusting your filters.
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>

              {matchesTotal > 0 && !matchesError && (
                <Pagination
                  loading={matchesLoading}
                  total={matchesTotal}
                  currentPage={matchCurrentPage}
                  itemsPerPage={matchItemsPerPage}
                  currentPageCount={currentMatchPageItems.length}
                  onPageChange={(page) => {
                    setMatchCurrentPage(page);
                  }}
                  pageSizeOptions={MATCH_PAGE_SIZE_OPTIONS}
                  onPageSizeChange={(size) => {
                    setMatchItemsPerPage(size);
                    setMatchCurrentPage(1);
                  }}
                  infoRenderer={({ start, end, total }) =>
                    `Showing ${start}-${end} of ${total} profiles`
                  }
                />
              )}
            </section>
          </>
        )}
      </main>

      {descriptionRole && (
        <div
          className="mmh-modal-overlay open mmh-role-description-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setDescriptionRole(null);
            }
          }}
        >
          <div
            className="mmh-modal-content mmh-role-description-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mmh-role-description-title"
          >
            <div className="mmh-modal-header">
              <h2 id="mmh-role-description-title" className="mmh-modal-title">
                {descriptionRole.title}
              </h2>

              <button
                type="button"
                className="mmh-role-description-modal-close"
                onClick={() => setDescriptionRole(null)}
                aria-label="Close role description"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mmh-modal-body mmh-role-description-modal-body">
              <p>
                {getRoleFullDescription(descriptionRole) ||
                  "No long description has been added for this role."}
              </p>
            </div>
          </div>
        </div>
      )}

      {showHiringOnboarding && (
        <div
          className="mmh-hiring-onboarding-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mmh-hiring-onboarding-title"
        >
          <div className="mmh-hiring-onboarding-modal">
            <div className="mmh-hiring-onboarding-header">
              <div>
                <span>Getting started with Hiring</span>

                <h2 id="mmh-hiring-onboarding-title">
                  {activeHiringOnboardingStep.title}
                </h2>
              </div>

              <button
                type="button"
                aria-label="Close onboarding"
                onClick={closeHiringOnboarding}
              >
                <X size={20} />
              </button>
            </div>

            <div className="mmh-hiring-onboarding-content">
              <div
                className="mmh-hiring-tutorial-steps"
                aria-label="Hiring tutorial steps"
              >
                {HIRING_ONBOARDING_STEPS.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index === hiringOnboardingStep;
                  const isCompleted = index < hiringOnboardingStep;

                  return (
                    <button
                      key={step.title}
                      type="button"
                      className={`mmh-hiring-tutorial-step ${
                        isActive ? "active" : ""
                      } ${isCompleted ? "completed" : ""}`}
                      onClick={() => setHiringOnboardingStep(index)}
                      aria-current={isActive ? "step" : undefined}
                    >
                      <span className="mmh-hiring-tutorial-step-icon">
                        {isCompleted ? (
                          <Check size={15} />
                        ) : (
                          <StepIcon size={15} />
                        )}
                      </span>

                      <span className="mmh-hiring-tutorial-step-copy">
                        <small>Step {index + 1}</small>
                        <strong>{step.title}</strong>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mmh-hiring-tutorial-detail">
                <div className="mmh-hiring-tutorial-detail-icon">
                  <HiringOnboardingIcon size={28} />
                </div>

                <div className="mmh-hiring-tutorial-detail-copy">
                  <span>
                    Step {hiringOnboardingStep + 1} of{" "}
                    {HIRING_ONBOARDING_STEPS.length}
                  </span>

                  <h3>{activeHiringOnboardingStep.title}</h3>

                  <p>{activeHiringOnboardingStep.description}</p>
                </div>
              </div>
            </div>

            <div className="mmh-hiring-onboarding-footer">
              <button
                type="button"
                className="mmh-btn mmh-btn-secondary"
                disabled={hiringOnboardingStep === 0}
                onClick={() =>
                  setHiringOnboardingStep((previous) => previous - 1)
                }
              >
                Back
              </button>

              {hiringOnboardingStep < HIRING_ONBOARDING_STEPS.length - 1 ? (
                <button
                  type="button"
                  className="mmh-btn mmh-btn-primary"
                  onClick={() =>
                    setHiringOnboardingStep((previous) => previous + 1)
                  }
                >
                  Next
                  <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  className="mmh-btn mmh-btn-primary"
                  onClick={closeHiringOnboarding}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default HiringPage;
